import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { PDFDocument } from 'pdf-lib';
import { useCallback, useEffect, useState } from 'react';
import React from 'react';
import { toast } from 'react-toastify';

import { SWLoadingButton } from '@/components/LoadingButton';
import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useGeneratePDF } from '@/hooks/useGeneratePDF';
import useMixPanel from '@/hooks/useMixPanel';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import {
  createStripeAccount,
  createStripeAccountLink,
  getUserInfo,
} from '@/pages/AccountSettings/api';
import { STRIPE_ACCOUNT_LINK_TYPE } from '@/pages/AccountSettings/types';
import { sendTenantReport } from '@/Structures/api';
import { useSubscribers } from '@/Structures/hooks/useSubscribers';
import { ISubscriber } from '@/Structures/subscribers/types';
import type { PmsConfig, PmsType } from '@/Structures/types';
import { StructureBilling } from '@/Structures/types';
import { Address, RateMethod } from '@/types';
import type { Unit as StructureUnit } from '@/Units/types';

import { convertArrayToCsvString, convertTimestampToTimeZone, naturalCompare } from '../../util';
import { formatNumber } from '../../util';
import {
  BatchCustomInvoiceInfo,
  CustomInvoiceLineItem,
  generateBatchCustomInvoices,
  GenerateBatchCustomInvoicesRequest,
  generateInvoicePDF,
  getCustomInvoiceDownloadUrls,
  sendCustomInvoiceEmails,
} from '../api';
import { hasPersistedInvoiceData } from '../customInvoiceAmounts';
import { formatDueDate, getDueDate } from '../dueDateUtils';
import {
  BillingContactWindow,
  computeOwnerShareSummary,
  computeValidBillingContactWindows,
} from '../occupancyPreview';
import { useBills } from '../useBills';
import { PayOnlineUrlField } from './PayOnlineUrlField';
import { PmsActions } from './PmsActions';
import { UsageGraphModeTable } from './UsageGraphModeTable';

interface StyleProps {
  isConfirm: boolean;
}

// style for the dialog paper/modal
const useStyles = makeStyles<Theme, StyleProps>((_theme) => ({
  paper: (props) => ({
    minHeight: props.isConfirm ? 'auto' : 560, // Auto height for confirm dialog
    maxHeight: '80vh',
    borderRadius: 10,
  }),
  dialogActionsRoot: {
    '@media print': {
      display: 'none',
    },
  },
}));

type Props = {
  bill: StructureBilling;
  onClose: () => void;
  open: boolean;
  structureName: string;
  autoDownload: 'csv' | 'pdf' | null;
  needReportButton: boolean;
  needSendBillButton: boolean;
  structureAddress: Address;
  timeZone: string;
  stripeConnect: boolean;
  invoices: any[];
  isPaymentReport: boolean;
  billsSentTimestamp: number | null;
  payOnlineUrl?: string | null;
  paymentInstructions?: string | null;
  dueDateDays?: number | null;
  onPaymentSettingsUpdate?: (
    payOnlineUrl: string | null,
    paymentInstructions: string | null,
    dueDateDays: number | null,
  ) => Promise<void>;
  pmsType?: PmsType | null;
  pmsConfig?: PmsConfig | null;
  onPmsSettingsUpdate?: (pmsType: PmsType | null, pmsConfig: PmsConfig | null) => Promise<void>;
  unitPmsNames?: Record<string, string>;
  unitPmsIds?: Record<string, number | string>;
  onRefreshBills?: () => void;
  structureUnits?: StructureUnit[];
  onStructureRefresh?: () => void;
};

interface IEventDetails {
  StartDate: string;
  EndDate: string;
  WaterUsage: number;
  VolumeUnit: string;
  Cost: number;
  RateMethod: RateMethod;
  PropertyName: string;
  PropertyID: string;
  BillID: string;
  EffectiveRate: number;
}

interface Device {
  serial_number: string;
  name: string;
  device_total_volume: number;
}

interface Unit {
  name: string;
  id: string;
  unit_total_volume: number;
  /** Support-entered estimated usage prorated to the bill period; SEPARATE from metered. */
  unit_estimated_volume?: number;
  /** Cost of the estimated volume at the effective rate; separate from unit_cost_meter. */
  unit_estimated_cost?: number;
  unit_cost: number;
  unit_cost_meter: number;
  devices: Device[];
  stripeLastStatus: string;
  stripeTotal: number;
  stripeCurrency: string;
  stripeInvoiceId: string;
  stripeInvoicePdfUrl: string;
  // TODO: Uncomment when stripe_invoice_url feature is ready for deployment
  // stripeInvoiceUrl: string;
  stripeCustomerId: string;
  unitId: string;
  unit_flags?: string[];
  is_billable?: boolean;
  /** Stored unit-level setting for the daily usage bar chart on invoice PDFs (0 = HIDDEN, 1 = INCLUDED). */
  usage_graph_mode?: number;
}

interface CommonAreaUsageData {
  name: string;
  unit_id: string;
  total_volume: number;
  split_volume: number;
  split_cost: number;
}

interface BillLineItem {
  id: string;
  type: string;
  description: string;
  cost?: number;
  cost_units?: string;
  volume?: number;
  volume_units?: string;
  rate_method?: string;
  effective_rate?: number;
  unit_id?: string; // snake_case from generateBill API
}

interface IUpdatedBillingData {
  billing_id: string;
  structure_id: string;
  structure_name: string;
  start_time: number;
  end_time: number;
  volume: number;
  volume_units: string;
  cost: number;
  cost_units: string;
  meter_total_volume: number;
  rate_method: string;
  effective_rate: number;
  units: Unit[];
  billable_units_count?: number;
  common_area_usage?: CommonAreaUsageData[];
  line_items?: BillLineItem[]; // Line items from generateBill API (snake_case)
}

type IStatusCount = {
  [key in 'not_created' | 'draft' | 'open' | 'paid' | 'uncollectible' | 'void']?: number;
};

// Meters trickle data; only flag an invoice's metered volume as stale when the
// drift exceeds this, so a sub-gallon straggler doesn't nag anyone.
const METERED_STALENESS_TOLERANCE_GALLONS = 1;

export const Breakdown = ({
  bill,
  structureName,
  open,
  onClose,
  autoDownload,
  needReportButton,
  needSendBillButton,
  structureAddress,
  timeZone,
  stripeConnect,
  invoices,
  isPaymentReport,
  billsSentTimestamp,
  payOnlineUrl,
  paymentInstructions,
  dueDateDays,
  onPaymentSettingsUpdate,
  pmsType,
  pmsConfig,
  onPmsSettingsUpdate,
  unitPmsNames,
  unitPmsIds,
  onRefreshBills,
  structureUnits,
  onStructureRefresh,
}: Props): JSX.Element => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const { toPDF, targetRef } = useGeneratePDF({ filename: `bill_report.pdf` });
  const formattedStartDate = convertTimestampToTimeZone(bill.start_time, timeZone);
  const formattedEndDate = convertTimestampToTimeZone(bill.end_time, timeZone);
  const title = `Tenant Report`;
  const [loading, setLoading] = React.useState<boolean>(false);
  const [tz] = React.useState(timeZone === '' ? moment.tz.guess() : timeZone);
  const [isConfirmReport, setIsConfirmReport] = React.useState<boolean>(false);
  const { sendBills } = useBills(bill.structure_id);
  const [updatedBill, setUpdatedBill] = React.useState<IUpdatedBillingData>();
  const [alredyCSV, setAlredyCSV] = React.useState(false);
  const [openConfirmSendInvoices, setOpenConfirmSendInvoices] = useState(false);
  const [openConnectStripe, setOpenConnectStripe] = useState(false);
  const [loadingConnect, setLoadingConnect] = useState(false);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(null);
  const { subscribers, hasLoaded: subscribersLoaded } = useSubscribers(bill.structure_id);
  const classes = useStyles({ isConfirm: isConfirmReport });
  const [localInvoices, setLocalInvoices] = useState(invoices);

  // Batch custom invoice generation state
  const [openBatchGenerateDialog, setOpenBatchGenerateDialog] = useState(false);
  const [batchPayOnlineUrl, setBatchPayOnlineUrl] = useState(payOnlineUrl || '');
  const [batchPaymentInstructions, setBatchPaymentInstructions] = useState(
    paymentInstructions || '',
  );
  const [batchDueDateDays, setBatchDueDateDays] = useState<number>(dueDateDays ?? 21);
  const [batchSaveAsDefault, setBatchSaveAsDefault] = useState(false);
  // Per-unit usage_graph_mode overrides for this batch.
  // Keyed by unit id; values are 0 (HIDDEN) or 1 (INCLUDED). Only contains entries
  // where the user has flipped the value away from the unit's stored default.
  const [batchUnitGraphModeOverrides, setBatchUnitGraphModeOverrides] = useState<
    Record<string, number>
  >({});
  // When true, persist the per-unit overrides back to the units table on Generate.
  const [batchSaveGraphModeOverrides, setBatchSaveGraphModeOverrides] = useState(false);
  // Track if user has modified the fields (to distinguish empty from "use default")
  const [batchUrlTouched, setBatchUrlTouched] = useState(false);
  const [batchInstructionsTouched, setBatchInstructionsTouched] = useState(false);
  const [isBatchGenerating, setIsBatchGenerating] = useState(false);
  const [batchCustomInvoices, setBatchCustomInvoices] = useState<BatchCustomInvoiceInfo[]>([]);
  const [batchGenerationStarted, setBatchGenerationStarted] = useState(false);
  const [batchGenerationProgress, setBatchGenerationProgress] = useState<{
    ready: number;
    total: number;
  } | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  // Track when Stripe invoices are being sent (before PDF generation starts)
  const [isSendingStripeInvoices, setIsSendingStripeInvoices] = useState(false);
  // Local state to track when bills have been sent (overrides prop for immediate UI update)
  const [localBillsSentTimestamp, setLocalBillsSentTimestamp] = useState<number | null>(
    billsSentTimestamp,
  );
  // Use local state if set, otherwise fall back to prop
  const effectiveBillsSentTimestamp = localBillsSentTimestamp ?? billsSentTimestamp;

  // Track which invoices have pending custom invoice records (not_created status)
  // This is used to disable individual generate buttons when batch generation is in progress
  const [pendingCustomInvoiceIds, setPendingCustomInvoiceIds] = useState<Set<string>>(new Set());
  // True while the batch dialog is being submitted in "regenerate" mode (after tenant
  // occupancy changes invalidated the previously-generated invoices).
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Org name for PDF filenames
  const [orgName, setOrgName] = useState('');
  React.useEffect(() => {
    getUserInfo().then((info) => setOrgName(info.user.organization_name));
  }, []);

  // Track actual due dates for custom invoices (keyed by billing_invoice_id)
  // This stores the real due date used when the invoice was generated
  const [customInvoiceDueDates, setCustomInvoiceDueDates] = useState<Map<string, string>>(
    new Map(),
  );
  // Track the actual per-tenant prorated amount rendered on each PDF (keyed by
  // billing_invoice_id -> cents). Populated from the download-urls polling response.
  // For multi-tenant prorated units this is the source of truth for the "Invoice
  // Amount" column; for legacy invoices generated before this field existed the
  // map will not contain an entry and callers fall back to the un-prorated total.
  const [customInvoiceAmountCents, setCustomInvoiceAmountCents] = useState<Map<string, number>>(
    new Map(),
  );
  // Per-category breakdown of each invoice's amount_due_cents (water usage, billable
  // common area, fees). Populated alongside customInvoiceAmountCents from the polling
  // response. When present, the bill breakdown tooltip uses these values directly
  // (collapsed 3-line view) so per-line numbers match the PDF for prorated invoices.
  const [customInvoiceBreakdown, setCustomInvoiceBreakdown] = useState<
    Map<string, { usageCents: number; commonAreaCents: number; feeCents: number }>
  >(new Map());
  // The frozen rendered lines of each invoice PDF, keyed by billing_invoice_id.
  // When present, rows and tooltips display these verbatim; the ratio-scaled
  // reconstruction below survives only for invoices rendered before the
  // line-item snapshot table existed.
  const [customInvoiceLineItems, setCustomInvoiceLineItems] = useState<
    Map<string, CustomInvoiceLineItem[]>
  >(new Map());
  // Clipped occupancy window the PDF was rendered against, keyed by
  // billing_invoice_id (YYYY-MM-DD strings, inclusive at both ends). Drives
  // the window-mismatch check in staleOccupancyUnits: when a PM edits a
  // tenant's move-in / move-out after generation, the current clipped window
  // for the same contact no longer matches the stored snapshot and the regen
  // banner fires. Absent for legacy invoices and for vacancy tracking rows
  // (no subscriber attached); those cases fall through to count / identity.
  // Whether each invoice's persisted estimated-usage fingerprint no longer
  // matches the freshly computed value (estimates added / edited / removed
  // after the PDF was rendered). Keyed by billing_invoice_id; absent for
  // legacy invoices without the snapshot (check skipped). Drives regen
  // check #6 in staleOccupancyUnits.
  const [customInvoiceEstimatedStale, setCustomInvoiceEstimatedStale] = useState<
    Map<string, boolean>
  >(new Map());
  // Same for metered usage (late meter data; regen check #7).
  const [customInvoiceMeteredStale, setCustomInvoiceMeteredStale] = useState<Map<string, boolean>>(
    new Map(),
  );
  const [customInvoiceOccupancyWindow, setCustomInvoiceOccupancyWindow] = useState<
    Map<string, { startDate: string; endDate: string }>
  >(new Map());

  // Selection state for batch download
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(new Set());

  // Send invoices by email state
  const [openSendEmailDialog, setOpenSendEmailDialog] = useState(false);
  const [isSendingEmails, setIsSendingEmails] = useState(false);
  // Track email-sent timestamps from DB (keyed by billing_invoice_id -> epoch seconds)
  const [emailSentMap, setEmailSentMap] = useState<Map<string, number>>(new Map());
  // Snapshot of payment info (URL + instructions) that was used when each
  // custom invoice was generated, keyed by billing_invoice_id. Backs the "i"
  // tooltip in the Invoice column so users can see what URL a past bill used.
  const [customInvoicePaymentInfoMap, setCustomInvoicePaymentInfoMap] = useState<
    Map<string, { payOnlineUrl: string | null; paymentInstructions: string | null }>
  >(new Map());

  // Sync local invoices with prop changes and auto-select all for non-Stripe
  React.useEffect(() => {
    setLocalInvoices(invoices);
    // Auto-select all invoices for non-Stripe accounts when invoices are loaded
    if (!stripeConnect && invoices.length > 0) {
      const allInvoiceIds = new Set(invoices.map((inv) => inv.id).filter(Boolean) as string[]);
      setSelectedInvoiceIds(allInvoiceIds);
    }
  }, [invoices, stripeConnect]);

  // Sync local billsSentTimestamp with prop changes (e.g., parent data refresh)
  React.useEffect(() => {
    if (billsSentTimestamp) {
      setLocalBillsSentTimestamp(billsSentTimestamp);
    }
  }, [billsSentTimestamp]);

  // Check for pending custom invoices when dialog opens or invoices change
  // This helps detect custom invoices that were started in a previous session
  // Also fetches due dates for all invoices (even those with PDFs already)
  React.useEffect(() => {
    // Skip if dialog is closed, no billing ID, no invoices, or already tracking batch generation
    if (!open || !bill?.billing_id || invoices.length === 0) {
      return;
    }

    // If batch generation is already in progress, the polling effect handles status
    // Don't query and overwrite pendingCustomInvoiceIds
    if (batchGenerationStarted) {
      return;
    }

    // Get ALL invoice IDs to fetch due dates (component remounts on dialog reopen, losing state)
    const allInvoiceIds = invoices.filter((inv) => inv.id).map((inv) => inv.id);

    if (allInvoiceIds.length === 0) {
      setPendingCustomInvoiceIds(new Set());
      return;
    }

    // Query the backend to check custom invoice status and get due dates for ALL invoices
    getCustomInvoiceDownloadUrls(bill.billing_id, allInvoiceIds)
      .then((result) => {
        // Find invoices that have custom invoice records but PDF not ready yet
        const pendingIds = new Set(
          result.custom_invoices
            .filter((ci) => ci.status === 'not_created')
            .map((ci) => ci.billing_invoice_id),
        );
        setPendingCustomInvoiceIds(pendingIds);

        // Store due dates for custom invoices that have them
        const dueDates = new Map<string, string>();
        result.custom_invoices.forEach((ci) => {
          if (ci.due_date) {
            dueDates.set(ci.billing_invoice_id, ci.due_date);
          }
        });
        if (dueDates.size > 0) {
          setCustomInvoiceDueDates((prev) => {
            const merged = new Map(prev);
            dueDates.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }

        // Store email-sent timestamps
        const emailSent = new Map<string, number>();
        result.custom_invoices.forEach((ci) => {
          if (ci.email_sent_at) {
            emailSent.set(ci.billing_invoice_id, ci.email_sent_at);
          }
        });
        if (emailSent.size > 0) {
          setEmailSentMap((prev) => {
            const merged = new Map(prev);
            emailSent.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }

        // Store per-invoice prorated amounts so the "Invoice Amount" column shows
        // each tenant's actual share rather than the un-prorated unit total.
        const amounts = new Map<string, number>();
        // Capture per-category breakdown alongside so the tooltip shows per-line
        // numbers that exactly match the PDF on prorated invoices.
        const breakdowns = new Map<
          string,
          { usageCents: number; commonAreaCents: number; feeCents: number }
        >();
        // Capture the clipped occupancy window the PDF was rendered against so
        // the regen banner can fire when tenant dates change after generation.
        const windows = new Map<string, { startDate: string; endDate: string }>();
        const lineItemsMaps = new Map<string, CustomInvoiceLineItem[]>();
        // Compare the estimated-usage fingerprint against the fresh value in
        // cents-free volume space (2dp, matching the backend's quantize)
        const estimatedStale = new Map<string, boolean>();
        const meteredStale = new Map<string, boolean>();
        result.custom_invoices.forEach((ci) => {
          if (
            ci.estimated_usage_volume_gallons !== null &&
            ci.estimated_usage_volume_gallons !== undefined &&
            ci.current_estimated_usage_volume_gallons !== null &&
            ci.current_estimated_usage_volume_gallons !== undefined
          ) {
            estimatedStale.set(
              ci.billing_invoice_id,
              Math.round(ci.estimated_usage_volume_gallons * 100) !==
                Math.round(ci.current_estimated_usage_volume_gallons * 100),
            );
          }
          if (
            ci.metered_usage_volume_gallons !== null &&
            ci.metered_usage_volume_gallons !== undefined &&
            ci.current_metered_usage_volume_gallons !== null &&
            ci.current_metered_usage_volume_gallons !== undefined
          ) {
            meteredStale.set(
              ci.billing_invoice_id,
              Math.abs(ci.metered_usage_volume_gallons - ci.current_metered_usage_volume_gallons) >
                METERED_STALENESS_TOLERANCE_GALLONS,
            );
          }
        });
        result.custom_invoices.forEach((ci) => {
          if (ci.amount_due_cents !== null && ci.amount_due_cents !== undefined) {
            amounts.set(ci.billing_invoice_id, ci.amount_due_cents);
          }
          if (
            ci.usage_cost_cents !== null &&
            ci.usage_cost_cents !== undefined &&
            ci.billable_common_area_cost_cents !== null &&
            ci.billable_common_area_cost_cents !== undefined &&
            ci.fee_cost_cents !== null &&
            ci.fee_cost_cents !== undefined
          ) {
            breakdowns.set(ci.billing_invoice_id, {
              usageCents: ci.usage_cost_cents,
              commonAreaCents: ci.billable_common_area_cost_cents,
              feeCents: ci.fee_cost_cents,
            });
          }
          if (ci.occupancy_start_date && ci.occupancy_end_date) {
            windows.set(ci.billing_invoice_id, {
              startDate: ci.occupancy_start_date,
              endDate: ci.occupancy_end_date,
            });
          }
          if (ci.line_items && ci.line_items.length > 0) {
            lineItemsMaps.set(ci.billing_invoice_id, ci.line_items);
          }
        });
        if (amounts.size > 0) {
          setCustomInvoiceAmountCents((prev) => {
            const merged = new Map(prev);
            amounts.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }
        if (breakdowns.size > 0) {
          setCustomInvoiceBreakdown((prev) => {
            const merged = new Map(prev);
            breakdowns.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }
        if (lineItemsMaps.size > 0) {
          setCustomInvoiceLineItems((prev) => {
            const merged = new Map(prev);
            lineItemsMaps.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }
        if (windows.size > 0) {
          setCustomInvoiceOccupancyWindow((prev) => {
            const merged = new Map(prev);
            windows.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }
        if (estimatedStale.size > 0) {
          setCustomInvoiceEstimatedStale((prev) => {
            const merged = new Map(prev);
            estimatedStale.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }
        if (meteredStale.size > 0) {
          setCustomInvoiceMeteredStale((prev) => {
            const merged = new Map(prev);
            meteredStale.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }

        // Store payment-info snapshots so the row tooltip can show what URL
        // / instructions a past invoice actually used.
        const paymentInfo = new Map<
          string,
          { payOnlineUrl: string | null; paymentInstructions: string | null }
        >();
        result.custom_invoices.forEach((ci) => {
          if (ci.pay_online_url || ci.payment_instructions) {
            paymentInfo.set(ci.billing_invoice_id, {
              payOnlineUrl: ci.pay_online_url,
              paymentInstructions: ci.payment_instructions,
            });
          }
        });
        if (paymentInfo.size > 0) {
          setCustomInvoicePaymentInfoMap((prev) => {
            const merged = new Map(prev);
            paymentInfo.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }

        // If there are pending invoices, resume polling
        if (pendingIds.size > 0) {
          // Set up state to trigger the polling effect
          const pendingInvoices: BatchCustomInvoiceInfo[] = result.custom_invoices
            .filter((ci) => ci.status === 'not_created' && ci.id)
            .map((ci) => ({
              custom_invoice_id: ci.id!,
              billing_invoice_id: ci.billing_invoice_id,
              status: 'not_created' as const,
            }));
          if (pendingInvoices.length > 0) {
            setBatchCustomInvoices(pendingInvoices);
            setBatchGenerationStarted(true);
          }
        }
      })
      .catch(() => {
        // Silently handle errors - this is a best-effort check
      });
  }, [open, bill?.billing_id, invoices, batchGenerationStarted]);

  // Sync batch payment settings with prop changes
  React.useEffect(() => {
    setBatchPayOnlineUrl(payOnlineUrl || '');
    setBatchPaymentInstructions(paymentInstructions || '');
    setBatchDueDateDays(dueDateDays ?? 21);
  }, [payOnlineUrl, paymentInstructions, dueDateDays]);

  // Ref to track if generation has completed (prevents duplicate toasts)
  const generationCompletedRef = React.useRef(false);

  // Store onRefreshBills in a ref to avoid dependency issues
  const onRefreshBillsRef = React.useRef(onRefreshBills);
  React.useEffect(() => {
    onRefreshBillsRef.current = onRefreshBills;
  }, [onRefreshBills]);

  // Poll for batch generation progress after generation starts
  React.useEffect(() => {
    if (!batchGenerationStarted || batchCustomInvoices.length === 0 || !bill?.billing_id) {
      return;
    }

    // Reset completion flag when starting new generation
    generationCompletedRef.current = false;

    // Get billing_invoice_ids from the batch response
    const billingInvoiceIds = batchCustomInvoices.map((inv) => inv.billing_invoice_id);
    if (billingInvoiceIds.length === 0) return;

    setIsPolling(true);
    setBatchGenerationProgress({ ready: 0, total: billingInvoiceIds.length });

    const pollInterval = setInterval(async () => {
      // Skip if already completed
      if (generationCompletedRef.current) {
        clearInterval(pollInterval);
        return;
      }

      try {
        const result = await getCustomInvoiceDownloadUrls(bill.billing_id, billingInvoiceIds);
        const readyCount = result.ready_count;
        const totalCount = billingInvoiceIds.length;

        setBatchGenerationProgress({ ready: readyCount, total: totalCount });

        // Store due dates for custom invoices that have them
        const dueDates = new Map<string, string>();
        result.custom_invoices.forEach((ci) => {
          if (ci.due_date) {
            dueDates.set(ci.billing_invoice_id, ci.due_date);
          }
        });
        if (dueDates.size > 0) {
          setCustomInvoiceDueDates((prev) => {
            const merged = new Map(prev);
            dueDates.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }

        // Store email-sent timestamps
        const emailSent = new Map<string, number>();
        result.custom_invoices.forEach((ci) => {
          if (ci.email_sent_at) {
            emailSent.set(ci.billing_invoice_id, ci.email_sent_at);
          }
        });
        if (emailSent.size > 0) {
          setEmailSentMap((prev) => {
            const merged = new Map(prev);
            emailSent.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }

        // Store per-invoice prorated amounts so the "Invoice Amount" column shows
        // each tenant's actual share rather than the un-prorated unit total.
        const amounts = new Map<string, number>();
        // Capture per-category breakdown alongside so the tooltip shows per-line
        // numbers that exactly match the PDF on prorated invoices.
        const breakdowns = new Map<
          string,
          { usageCents: number; commonAreaCents: number; feeCents: number }
        >();
        // Capture the clipped occupancy window the PDF was rendered against so
        // the regen banner can fire when tenant dates change after generation.
        const windows = new Map<string, { startDate: string; endDate: string }>();
        const lineItemsMaps = new Map<string, CustomInvoiceLineItem[]>();
        // Compare the estimated-usage fingerprint against the fresh value in
        // cents-free volume space (2dp, matching the backend's quantize)
        const estimatedStale = new Map<string, boolean>();
        const meteredStale = new Map<string, boolean>();
        result.custom_invoices.forEach((ci) => {
          if (
            ci.estimated_usage_volume_gallons !== null &&
            ci.estimated_usage_volume_gallons !== undefined &&
            ci.current_estimated_usage_volume_gallons !== null &&
            ci.current_estimated_usage_volume_gallons !== undefined
          ) {
            estimatedStale.set(
              ci.billing_invoice_id,
              Math.round(ci.estimated_usage_volume_gallons * 100) !==
                Math.round(ci.current_estimated_usage_volume_gallons * 100),
            );
          }
          if (
            ci.metered_usage_volume_gallons !== null &&
            ci.metered_usage_volume_gallons !== undefined &&
            ci.current_metered_usage_volume_gallons !== null &&
            ci.current_metered_usage_volume_gallons !== undefined
          ) {
            meteredStale.set(
              ci.billing_invoice_id,
              Math.abs(ci.metered_usage_volume_gallons - ci.current_metered_usage_volume_gallons) >
                METERED_STALENESS_TOLERANCE_GALLONS,
            );
          }
        });
        result.custom_invoices.forEach((ci) => {
          if (ci.amount_due_cents !== null && ci.amount_due_cents !== undefined) {
            amounts.set(ci.billing_invoice_id, ci.amount_due_cents);
          }
          if (
            ci.usage_cost_cents !== null &&
            ci.usage_cost_cents !== undefined &&
            ci.billable_common_area_cost_cents !== null &&
            ci.billable_common_area_cost_cents !== undefined &&
            ci.fee_cost_cents !== null &&
            ci.fee_cost_cents !== undefined
          ) {
            breakdowns.set(ci.billing_invoice_id, {
              usageCents: ci.usage_cost_cents,
              commonAreaCents: ci.billable_common_area_cost_cents,
              feeCents: ci.fee_cost_cents,
            });
          }
          if (ci.occupancy_start_date && ci.occupancy_end_date) {
            windows.set(ci.billing_invoice_id, {
              startDate: ci.occupancy_start_date,
              endDate: ci.occupancy_end_date,
            });
          }
          if (ci.line_items && ci.line_items.length > 0) {
            lineItemsMaps.set(ci.billing_invoice_id, ci.line_items);
          }
        });
        if (amounts.size > 0) {
          setCustomInvoiceAmountCents((prev) => {
            const merged = new Map(prev);
            amounts.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }
        if (breakdowns.size > 0) {
          setCustomInvoiceBreakdown((prev) => {
            const merged = new Map(prev);
            breakdowns.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }
        if (lineItemsMaps.size > 0) {
          setCustomInvoiceLineItems((prev) => {
            const merged = new Map(prev);
            lineItemsMaps.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }
        if (windows.size > 0) {
          setCustomInvoiceOccupancyWindow((prev) => {
            const merged = new Map(prev);
            windows.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }
        if (estimatedStale.size > 0) {
          setCustomInvoiceEstimatedStale((prev) => {
            const merged = new Map(prev);
            estimatedStale.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }
        if (meteredStale.size > 0) {
          setCustomInvoiceMeteredStale((prev) => {
            const merged = new Map(prev);
            meteredStale.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }

        // Store payment-info snapshots so the row tooltip can show what URL
        // / instructions a past invoice actually used.
        const paymentInfo = new Map<
          string,
          { payOnlineUrl: string | null; paymentInstructions: string | null }
        >();
        result.custom_invoices.forEach((ci) => {
          if (ci.pay_online_url || ci.payment_instructions) {
            paymentInfo.set(ci.billing_invoice_id, {
              payOnlineUrl: ci.pay_online_url,
              paymentInstructions: ci.payment_instructions,
            });
          }
        });
        if (paymentInfo.size > 0) {
          setCustomInvoicePaymentInfoMap((prev) => {
            const merged = new Map(prev);
            paymentInfo.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        }

        // Stop polling when all are ready
        if (readyCount >= totalCount && !generationCompletedRef.current) {
          generationCompletedRef.current = true;
          clearInterval(pollInterval);
          setIsPolling(false);
          // Clear pending set since all are now ready
          setPendingCustomInvoiceIds(new Set());
          toast.success('All SSW invoice PDFs are ready!', { closeButton: true });
          // Refresh bills data so the invoices show up in the table
          if (onRefreshBillsRef.current) {
            onRefreshBillsRef.current();
          }
          // Do NOT reset batchGenerationStarted/batchCustomInvoices/batchGenerationProgress
          // here — the dialog may still be open, and flipping batchGenerationStarted back
          // to false would re-render the original "Generate Invoices" form on top of the
          // success state. handleOpenBatchGenerateDialog resets those fields on next open.
        }
      } catch (error) {
        console.error('Error polling for invoice status:', error);
        // Don't stop polling on error, just log it
      }
    }, 3000); // Poll every 3 seconds

    return () => {
      clearInterval(pollInterval);
      setIsPolling(false);
    };
  }, [batchGenerationStarted, batchCustomInvoices, bill?.billing_id]);

  // Callback to update invoice's customPdfUrl after generation
  const handleInvoicePdfGenerated = React.useCallback((invoiceId: string, pdfUrl: string) => {
    setLocalInvoices((prev) =>
      prev.map((inv) => (inv.id === invoiceId ? { ...inv, customInvoicePdfUrl: pdfUrl } : inv)),
    );
  }, []);

  // Single source of truth: subscriber lookup by ID.
  // Used by both the breakdown table rows and the email confirmation dialog.
  const subscriberById = React.useMemo(() => {
    return new Map(
      subscribers
        .filter((sub): sub is ISubscriber & { id: string } => !!sub.id)
        .map((sub) => [sub.id, sub]),
    );
  }, [subscribers]);

  // Map of unit_id -> valid billing contacts (with clipped occupancy windows) for
  // this billing period. The selection logic lives in occupancyPreview.ts and
  // mirrors backend BillingManager.get_valid_billing_contacts under the
  // occupancy-driven model (revised 2026-06-02). Inputs are property-local
  // YYYY-MM-DD strings, NOT UTC milliseconds: the backend stores
  // bill.start_time / end_time as midnight in the property's timezone
  // (e.g. America/Denver = 6am UTC), and move_in / move_out arrive as raw
  // YYYY-MM-DD strings the backend also writes in property-local date space.
  // Comparing in UTC ms blends the two and shifts by the tz offset, breaking
  // 1-day occupancies at the period edge (move_in Jun 1 / move_out Jun 2 vs
  // bill starting Jun 1 in a -6 zone collapses into windowEnd < windowStart
  // and the tenant gets dropped from valid contacts).
  const billingContactWindowsByUnit = React.useMemo<Map<string, BillingContactWindow[]>>(() => {
    if (!bill.start_time || !bill.end_time) return new Map();
    const tzDate = (epochSec: number): string =>
      new Intl.DateTimeFormat('en-CA', {
        timeZone: timeZone || 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date(epochSec * 1000));
    return computeValidBillingContactWindows(
      subscribers,
      tzDate(bill.start_time),
      tzDate(bill.end_time),
    );
  }, [subscribers, bill.start_time, bill.end_time, timeZone]);

  // Map of unit_id -> set of subscriber_ids who are currently valid billing contacts.
  // Used to (a) detect stale invoices whose stored subscriber is no longer a valid
  // contact, and (b) resolve the current recipient at email-display time so the UI
  // matches what the backend will actually send.
  const validBillingContactsByUnit = React.useMemo<Map<string, Set<string>>>(() => {
    const result = new Map<string, Set<string>>();
    billingContactWindowsByUnit.forEach((windows, unitId) => {
      result.set(unitId, new Set(windows.map((w) => w.subscriberId)));
    });
    return result;
  }, [billingContactWindowsByUnit]);

  // Units on this bill whose prospective invoices will not cover the whole billing
  // period (a billed tenant moved in after the period started or out before it
  // ended). Drives the pre-generation heads-up that invoice totals will come out
  // below the bill's Total Cost.
  const partialOccupancyUnitNames = React.useMemo<string[]>(() => {
    if (!subscribersLoaded) return [];
    const names: string[] = [];
    for (const unit of bill.units || []) {
      const windows = billingContactWindowsByUnit.get(unit.id);
      if (windows && windows.some((w) => w.isPartialPeriod)) names.push(unit.name);
    }
    return names;
  }, [bill.units, billingContactWindowsByUnit, subscribersLoaded]);

  // Post-generation reconciliation: persisted (as-issued) invoice totals vs the
  // live full-period computation. Non-null only once download-urls has supplied
  // persisted amounts; the difference is the owner-absorbed share (vacancy days,
  // mid-period turnover gaps).
  const ownerShareSummary = React.useMemo(() => {
    if (customInvoiceAmountCents.size === 0) return null;
    const units = ((updatedBill ?? bill)?.units ?? []) as Array<{
      id: string;
      unit_cost_meter?: number | null;
      unit_estimated_cost?: number | null;
      unit_cost?: number | null;
    }>;
    return computeOwnerShareSummary(units, localInvoices, customInvoiceAmountCents);
  }, [updatedBill, bill, localInvoices, customInvoiceAmountCents]);

  // Detect units whose previously-generated invoices no longer match the current
  // state of the property. Four flavors of mismatch:
  //   1. Count differs: a tenant was added/removed since generation, so the number of
  //      invoices needs to grow or shrink (e.g., mid-cycle move-in added).
  //   2. Identity differs: the stored subscriber on an existing invoice is no longer a
  //      valid billing contact for this period (e.g., their move-in date was moved
  //      outside the period), so the invoice currently routes to the wrong tenant.
  //   3. Window differs: same contact remains valid, but the clipped occupancy
  //      window the PDF was rendered against has shifted (e.g., a move-out was
  //      added mid-cycle after generation, so a full-month invoice should now be
  //      prorated). Count + identity both pass in this case since the tenant is
  //      still the right person; only the window snapshot from the PDF render
  //      reveals the drift. Requires occupancy_start/end_date on the invoice;
  //      silently skipped for legacy rows without the snapshot.
  //   4. Missing entirely: a billable unit exists in the current structure but has no
  //      invoice row for this bill (e.g., the unit was added or had its billable flag
  //      toggled on after the bill was generated). The bill page shows the unit with
  //      live-computed usage, but no invoice was ever created for it.
  // Any of the four triggers the regen warning.
  const staleOccupancyUnits = React.useMemo<
    {
      unitName: string;
      expected: number;
      actual: number;
      reason: 'occupancy' | 'estimated' | 'metered';
    }[]
  >(() => {
    if (stripeConnect) return []; // Stripe path is being sunsetted; not supported
    if (!localInvoices || localInvoices.length === 0) return [];
    // Until the subscribers fetch resolves, validBillingContactsByUnit is empty
    // and every stored subscriber looks invalid; computing now flashes the
    // banner on first open and clears it a beat later
    if (!subscribersLoaded) return [];

    if (!bill.start_time || !bill.end_time) return [];

    // Property-local YYYY-MM-DD bounds, matching the backend's
    // _clip_occupancy_window snapshot semantics (see validBillingContactsByUnit
    // above for the timezone-drift backstory).
    const tzDate = (epochSec: number): string =>
      new Intl.DateTimeFormat('en-CA', {
        timeZone: timeZone || 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date(epochSec * 1000));
    const billingStartIso = tzDate(bill.start_time);
    const billingEndIso = tzDate(bill.end_time);

    const actualByUnit = new Map<string, number>();
    const invoiceSubscribersByUnit = new Map<string, string[]>();
    for (const inv of localInvoices) {
      // The bills API normalises invoices to camelCase (unitId), but test fixtures
      // and some adapters still pass snake_case (unit_id). Accept either to keep
      // detection accurate regardless of how the invoice list was constructed.
      const invUnitId: string | undefined = inv.unitId ?? inv.unit_id;
      if (!invUnitId) continue;
      actualByUnit.set(invUnitId, (actualByUnit.get(invUnitId) ?? 0) + 1);
      if (inv.subscriberId) {
        if (!invoiceSubscribersByUnit.has(invUnitId)) invoiceSubscribersByUnit.set(invUnitId, []);
        invoiceSubscribersByUnit.get(invUnitId)!.push(inv.subscriberId);
      }
    }

    const unitNameById = new Map<string, string>();
    for (const u of bill.units || []) {
      unitNameById.set(u.id, u.name);
    }

    const stale: {
      unitName: string;
      expected: number;
      actual: number;
      reason: 'occupancy' | 'estimated' | 'metered';
    }[] = [];
    actualByUnit.forEach((actual, unitId) => {
      const validContacts = validBillingContactsByUnit.get(unitId) ?? new Set<string>();
      const expected = validContacts.size;

      // Count mismatch (e.g., a new tenant was added or removed mid-cycle).
      // The expected === 0 && actual === 1 case is allowed through the COUNT
      // check (it's not strictly a count anomaly), but identity is still
      // checked below: if the existing invoice points at a tenant who's no
      // longer valid for this period (move-out set before the period, or
      // tenant was removed entirely), the banner must fire.
      if (expected !== actual) {
        if (!(expected === 0 && actual === 1)) {
          stale.push({
            unitName: unitNameById.get(unitId) ?? unitId,
            expected,
            actual,
            reason: 'occupancy',
          });
          return;
        }
      }

      // Identity mismatch: an existing invoice's stored subscriber is no
      // longer a valid billing contact for this period (their occupancy
      // window changed, or the subscriber was deleted). Run this check
      // regardless of `expected`: when expected=0 the invoice is orphaned,
      // and the PM still needs the regen warning to clear it.
      const invoiceSubs = invoiceSubscribersByUnit.get(unitId) ?? [];
      const anyStale = invoiceSubs.some((subId) => !validContacts.has(subId));
      // Vacancy-invoice-but-contacts-exist mismatch: at least one existing
      // invoice has no stored subscriber (it was generated when the unit
      // had no valid contact) but the unit now has one or more valid
      // contacts. Count and identity both pass coincidentally — count
      // because the per-unit invoice total equals the per-unit contact
      // total, identity because an empty `invoiceSubs` array can't
      // contain a stale ID. Without this branch the banner stays silent
      // even though the existing invoice has no recipient and the unit
      // has someone to bill.
      const invoiceCountForUnit = actualByUnit.get(unitId) ?? 0;
      const vacancyInvoiceButContactsExist =
        invoiceCountForUnit > invoiceSubs.length && validContacts.size > 0;
      if (anyStale || vacancyInvoiceButContactsExist) {
        stale.push({
          unitName: unitNameById.get(unitId) ?? unitId,
          expected,
          actual,
          reason: 'occupancy',
        });
        return;
      }

      // Window mismatch: count + identity both pass, but the clipped occupancy
      // window the PDF was rendered against (stored on the invoice at render
      // time) no longer matches what the current move-in / move-out dates
      // would produce. Catches the "single tenant, mid-cycle move-out added
      // after generation" case where the same person remains the valid
      // contact but the invoice's prorated math is now wrong. Mirrors the
      // backend's _clip_occupancy_window: start = max(move_in_date,
      // period_start) and end = min(move_out_date, period_end), with
      // period bounds substituted when the dates are null. Computed in the
      // property's local date space (same as the backend's snapshot) so the
      // comparison doesn't drift by the tz offset.
      for (const inv of localInvoices) {
        const invUnitId = inv.unitId ?? inv.unit_id;
        if (invUnitId !== unitId) continue;
        const storedWindow = customInvoiceOccupancyWindow.get(inv.id);
        if (!storedWindow) continue; // legacy invoice or vacancy tracking row
        const subId = inv.subscriberId;
        if (!subId) continue;
        const sub = subscriberById.get(subId);
        if (!sub) continue;
        const us = (sub.unit_subscriptions || []).find((u) => u.unit_id === unitId);
        if (!us) continue;
        const moveInIso = us.move_in_date || '';
        // move_out_date is the inclusive last day of lease (v2.0.1)
        const moveOutInclusiveIso = us.move_out_date || '';
        const currentStart = moveInIso && moveInIso > billingStartIso ? moveInIso : billingStartIso;
        const currentEnd =
          moveOutInclusiveIso && moveOutInclusiveIso < billingEndIso
            ? moveOutInclusiveIso
            : billingEndIso;
        if (currentEnd < currentStart) continue;
        // Invoices rendered before v2.0.1 stored the OLD exclusive-semantics
        // window (end = move_out - 1 day). A stored end exactly one day short
        // of the current end with an unchanged start is that legacy snapshot,
        // not a PM edit; without this every pre-v2.0.1 invoice with a move-out
        // date would prompt for regeneration on upgrade. Known trade-off: a
        // genuine one-day extension of a move-out won't prompt on its own.
        const legacyEnd = dayjs(currentEnd).subtract(1, 'day').format('YYYY-MM-DD');
        const endMatches =
          currentEnd === storedWindow.endDate || legacyEnd === storedWindow.endDate;
        if (currentStart !== storedWindow.startDate || !endMatches) {
          stale.push({
            unitName: unitNameById.get(unitId) ?? unitId,
            expected,
            actual,
            reason: 'occupancy',
          });
          return;
        }
      }
    });

    // Missing-invoice mismatch: billable unit in the current structure has no invoice
    // row for this bill. Happens when a unit was added (or had its billable flag
    // toggled on, or was moved between structures) after the bill was generated.
    for (const u of bill.units || []) {
      const isBillable = (u as any).is_billable ?? true;
      if (!isBillable) continue;
      if (!actualByUnit.has(u.id)) {
        stale.push({ unitName: u.name, expected: 1, actual: 0, reason: 'occupancy' });
      }
    }

    // Check #6: estimated-usage mismatch. The invoice's persisted estimated
    // volume fingerprint no longer matches the freshly computed current value,
    // meaning support added, edited, or removed estimated usage after the PDF
    // was rendered. Value-based (not timestamps: deletions and range edits
    // don't reliably bump any in-window timestamp) and volume-based (not
    // cents: immune to effective-rate drift from late meter data). Skipped
    // for invoices without the snapshot (rendered before it existed).
    const estimatedStaleUnitIds = new Set<string>();
    for (const inv of localInvoices) {
      if (!customInvoiceEstimatedStale.get(inv.id)) continue;
      const invUnitId = inv.unitId ?? inv.unit_id;
      if (invUnitId) estimatedStaleUnitIds.add(invUnitId);
    }
    estimatedStaleUnitIds.forEach((unitId) => {
      stale.push({
        unitName: unitNameById.get(unitId) ?? unitId,
        expected: actualByUnit.get(unitId) ?? 0,
        actual: actualByUnit.get(unitId) ?? 0,
        reason: 'estimated',
      });
    });

    // Check #7: metered mismatch beyond tolerance (late or corrected meter data)
    const meteredStaleUnitIds = new Set<string>();
    for (const inv of localInvoices) {
      if (!customInvoiceMeteredStale.get(inv.id)) continue;
      const invUnitId = inv.unitId ?? inv.unit_id;
      if (invUnitId) meteredStaleUnitIds.add(invUnitId);
    }
    meteredStaleUnitIds.forEach((unitId) => {
      stale.push({
        unitName: unitNameById.get(unitId) ?? unitId,
        expected: actualByUnit.get(unitId) ?? 0,
        actual: actualByUnit.get(unitId) ?? 0,
        reason: 'metered',
      });
    });

    stale.sort((a, b) => naturalCompare(a.unitName, b.unitName));
    return stale;
  }, [
    stripeConnect,
    localInvoices,
    subscribersLoaded,
    validBillingContactsByUnit,
    bill.start_time,
    bill.end_time,
    bill.units,
    customInvoiceOccupancyWindow,
    customInvoiceEstimatedStale,
    customInvoiceMeteredStale,
    subscriberById,
    timeZone,
  ]);

  // Build rows for the email confirmation table from selected invoices
  const selectedEmailRows = React.useMemo(() => {
    const unitMap = new Map<string, string>();
    (bill.units || []).forEach((unit) => {
      unitMap.set(unit.id, unit.name);
    });

    return localInvoices
      .filter((inv) => selectedInvoiceIds.has(inv.id))
      .map((inv) => {
        // Test fixtures and some adapters pass snake_case unit_id; the bills API
        // normalises to camelCase unitId. Accept either so the unit / contact
        // lookups work regardless of which shape the invoice was constructed from.
        const invUnitId = inv.unitId ?? inv.unit_id;
        const unitName = unitMap.get(invUnitId) || invUnitId || '';
        // Resolve the recipient using the CURRENT valid billing contacts for the unit.
        // The stored subscriber_id on the invoice is ONLY trusted when that subscriber
        // is still a valid contact for this billing period. Otherwise we'd silently
        // show (and send to) a past tenant whose move-out happened after the invoice
        // was generated. Order:
        //   1. Stored subscriber, IF still a valid contact for the period.
        //   2. Any currently valid contact for the unit (fallback for cases where the
        //      stored one was demoted/replaced).
        //   3. None: row is flagged as "no valid recipient" and will be skipped
        //      server-side. UI fades the row and surfaces a warning alert at the
        //      top; partial sends are allowed (Send is only disabled when every
        //      selected row would be skipped).
        const validForUnit = validBillingContactsByUnit.get(invUnitId);
        const storedId: string | undefined = inv.subscriberId ?? undefined;
        let resolvedId: string | undefined;
        if (storedId && validForUnit && validForUnit.has(storedId)) {
          resolvedId = storedId;
        } else if (validForUnit && validForUnit.size > 0) {
          resolvedId = validForUnit.values().next().value;
        }
        const sub = resolvedId ? subscriberById.get(resolvedId) : undefined;
        const noValidRecipient = !sub;
        return {
          unitName,
          fullName: sub ? `${sub.first_name} ${sub.last_name}` : 'No valid recipient',
          email: sub?.email || 'Will be skipped',
          noValidRecipient,
        };
      })
      .sort((a, b) => naturalCompare(a.unitName, b.unitName));
  }, [selectedInvoiceIds, localInvoices, subscriberById, bill.units, validBillingContactsByUnit]);

  // Selected invoices the backend will skip (no current valid recipient for
  // this period). Drives the soft warning alert at the top of the confirmation
  // dialog. Send isn't blocked on partial mismatches: the backend skips the
  // invalid ones and the post-send toast surfaces the skip count. Only disable
  // Send when EVERY selected row would be skipped (clicking would queue zero).
  const skippedCount = React.useMemo(
    () => selectedEmailRows.filter((row) => row.noValidRecipient).length,
    [selectedEmailRows],
  );
  const allWillBeSkipped =
    selectedEmailRows.length > 0 && skippedCount === selectedEmailRows.length;

  const alreadySentCount = React.useMemo(() => {
    return Array.from(selectedInvoiceIds).filter((id) => emailSentMap.has(id)).length;
  }, [selectedInvoiceIds, emailSentMap]);

  const {
    billingEnabled,
    stripeAccountExists,
    stripeSetupComplete,
    loading: billingStatusLoading,
  } = useBillingStatus();

  // run your merge whenever bill or localInvoices change
  // bill.line_items is used internally, so no need for separate lineItems dependency
  React.useEffect(() => {
    const enriched = mergeInvoicesToUnits(bill, localInvoices);
    setUpdatedBill(enriched);
  }, [bill, localInvoices]);

  const isDownloadMenuOpen = Boolean(downloadAnchorEl);

  const [eventDetails] = React.useState<IEventDetails>({
    StartDate: formattedStartDate,
    EndDate: formattedEndDate,
    WaterUsage: bill.volume,
    VolumeUnit: bill.volume_units,
    Cost: bill.cost,
    RateMethod: bill.rate_method,
    PropertyName: structureName,
    PropertyID: bill.structure_id,
    BillID: bill.billing_id,
    EffectiveRate: bill.effective_rate,
  });
  const { trackEvent } = useMixPanel();
  const mergeInvoicesToUnits = (bill: StructureBilling, invoices: any[]): IUpdatedBillingData => {
    // start by telling TS "I know this is IUpdatedBillingData"
    const updatedBill = { ...bill } as IUpdatedBillingData;

    // Use line_items from the bill (generateBill API) - snake_case
    // This ensures consistency since bill.units also comes from generateBill
    const billLineItems = bill.line_items ?? [];
    const splitItems = billLineItems.filter((item) => item.type === 'SPLIT');
    const flatItems = billLineItems.filter((item) => item.type === 'FLAT');
    const unitFlatItems = billLineItems.filter((item) => item.type === 'UNIT_FLAT');

    // Use billable_units_count from API if available, otherwise fallback to total units
    const billableUnitsCount = bill.billable_units_count ?? updatedBill.units.length;

    // Calculate total common area cost per billable unit (split among billable units)
    const commonAreaUsage = bill.common_area_usage ?? [];
    const commonAreaCostPerUnit = commonAreaUsage.reduce((sum, ca) => sum + ca.split_cost, 0);

    updatedBill.units = updatedBill.units.map((unit) => {
      let unitCost = 0;

      // Only add common area cost, split fees, and flat fees to billable units
      // Non-billable and common area units don't get these charges
      const isBillable = unit.is_billable ?? true; // Default to true for backwards compatibility

      if (isBillable) {
        // Add common area usage cost (already split per billable unit from API)
        unitCost += commonAreaCostPerUnit;

        splitItems.forEach((item) => {
          unitCost += (item.cost ?? 0) / billableUnitsCount;
        });

        flatItems.forEach((item) => {
          unitCost += item.cost ?? 0;
        });
      }

      // Unit flat fees are always for specific units
      // line_items come from generateBill API (snake_case) - same source as bill.units
      unitFlatItems.forEach((item) => {
        if (item.unit_id === unit.id) {
          unitCost += item.cost ?? 0;
        }
      });

      return { ...unit, unit_cost: unitCost };
    });

    // Calculate total cost = original water bill + fees for billable units
    // Start with original bill.cost (the actual water utility bill amount)
    // Then add fees that apply to billable units only
    const totalFees = updatedBill.units.reduce((acc, unit: any) => {
      const isBillable = unit.is_billable ?? true;
      if (!isBillable) return acc;
      // unit_cost includes: common area split + split fees + flat fees + unit flat fees
      // Subtract common area split to get just the fees
      const feesOnly = (unit.unit_cost || 0) - commonAreaCostPerUnit;
      return acc + feesOnly;
    }, 0);
    updatedBill.cost = bill.cost + totalFees;

    if (invoices && invoices.length > 0) {
      updatedBill.units = updatedBill.units.map((unit) => {
        // invoices come from bills API which converts to camelCase
        const matchingInvoice = invoices.find((invoice) => invoice.unitId === unit.id);
        if (matchingInvoice) {
          // Destructure to avoid overwriting unit.id with invoice.id
          const { id: _invoiceId, ...invoiceWithoutId } = matchingInvoice;
          return { ...unit, ...invoiceWithoutId };
        }
        return unit;
      });
    }

    return updatedBill;
  };

  const handleDownloadMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setDownloadAnchorEl(e.currentTarget);
  };
  const handleDownloadMenuClose = () => {
    setDownloadAnchorEl(null);
  };

  const handleDownloadPDF = () => {
    toPDF();
    trackEvent('Bill Downloaded', eventDetails);
    handleDownloadMenuClose();
  };

  const handleDownloadCSVMenu = () => {
    handleCsvDownload();
    trackEvent('Bill Downloaded', eventDetails);
    handleDownloadMenuClose();
  };

  const handleSendInvoices = async () => {
    // Close the confirmation sub-dialog
    setOpenConfirmSendInvoices(false);
    // Set loading state - the useEffect below will open the batch dialog
    setIsSendingStripeInvoices(true);
  };

  // Effect to open batch dialog and start sending when isSendingStripeInvoices becomes true
  useEffect(() => {
    if (isSendingStripeInvoices && !openBatchGenerateDialog) {
      setOpenBatchGenerateDialog(true);
      // Start the send process
      handleSendBill();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSendingStripeInvoices]);

  const handleCsvDownload = useCallback(async () => {
    // 1) merge the invoices into the bill (uses bill.line_items internally)
    const enrichedBill = mergeInvoicesToUnits(bill, localInvoices);

    // 2) pick enrichedBill (since updatedBill may not be set)
    const data = enrichedBill;

    // 3) Organize fees by type for consistent columns - use bill.line_items (snake_case)
    const billLineItems = bill.line_items ?? [];
    const splitFees = billLineItems.filter((item) => item.type === 'SPLIT');
    const flatFees = billLineItems.filter((item) => item.type === 'FLAT');
    const unitFlatFees = billLineItems.filter((item) => item.type === 'UNIT_FLAT');
    const hasAnyFees = splitFees.length + flatFees.length + unitFlatFees.length > 0;
    // Use billable_units_count from API if available, otherwise fallback to total units
    const billableUnitsCount = bill.billable_units_count ?? data.units.length;

    // 4) sort units alphanumerically and build CSV rows with water cost, itemized fees, and total
    const sortedUnits = [...data.units].sort((a, b) => naturalCompare(a.name, b.name));
    // Estimated usage exports as SEPARATE columns (never merged into Volume /
    // Water Usage Cost); columns appear only when the bill has estimates so
    // existing exports keep their exact shape
    const csvHasEstimates = sortedUnits.some((unit) => (unit.unit_estimated_volume ?? 0) > 0);

    // A custom invoice has "persisted invoice data" once the download-urls response has
    // returned at least one of amount_due_cents / the per-category breakdown / the frozen
    // line items for it. Presence of persisted data for ANY invoice on this bill means
    // custom invoices exist for the bill: persisted values become the source of truth for
    // every invoiced unit's row. When none of the bill's invoices carry persisted data (no
    // custom invoices generated yet), every row falls through to the unchanged live-compute
    // path below, exactly as before this feature existed. Shared with the PMS export
    // writers (pmsExport.ts) so both agree on when persisted data exists for a bill.
    const invoiceMaps = {
      customInvoiceAmountCents,
      customInvoiceBreakdown,
      customInvoiceLineItems,
    };
    const hasPersistedInvoiceDataForInvoice = (invoiceId: string | null | undefined): boolean =>
      hasPersistedInvoiceData(invoiceId, invoiceMaps);
    const hasCustomInvoicesForBill = localInvoices.some((inv: any) =>
      hasPersistedInvoiceDataForInvoice(inv?.id),
    );

    // Existing live-compute row for a unit: unchanged from the pre-invoice CSV. Used both
    // as the sole path when the bill has no custom invoices, and as the per-row fallback
    // for units that never got an invoice within an invoiced bill (e.g. common area rows).
    const buildComputedRow = (unit: any): any => {
      const baseRow: any = {
        'Unit Name': unit.name,
        'Structure Name': bill.structure_name,
        Volume: formatNumber(unit.unit_total_volume, 3),
        'Volume Units': bill.volume_units,
        'Effective Rate': formatNumber(bill.effective_rate, 2),
        'Total Cost': `$${formatNumber(
          (unit.unit_cost || 0) + (unit.unit_cost_meter || 0) + (unit.unit_estimated_cost || 0),
          2,
        )}`,
        'Cost Units': bill.cost_units,
        'Water Usage Cost': `$${formatNumber(unit.unit_cost_meter || 0, 2)}`,
      };
      if (csvHasEstimates) {
        baseRow['Estimated Volume'] = formatNumber(unit.unit_estimated_volume ?? 0, 3);
        baseRow['Estimated Usage Cost'] = `$${formatNumber(unit.unit_estimated_cost ?? 0, 2)}`;
      }

      if (hasAnyFees) {
        let feeIndex = 1;

        splitFees.forEach((fee) => {
          const feeAmount = (fee.cost ?? 0) / billableUnitsCount;
          baseRow[`Fee ${feeIndex} Name`] = fee.description || 'Split Fee';
          baseRow[`Fee ${feeIndex} Type`] = 'SPLIT';
          baseRow[`Fee ${feeIndex} Cost`] = `$${formatNumber(feeAmount, 2)}`;
          feeIndex++;
        });

        flatFees.forEach((fee) => {
          baseRow[`Fee ${feeIndex} Name`] = fee.description || 'Flat Fee';
          baseRow[`Fee ${feeIndex} Type`] = 'FLAT';
          baseRow[`Fee ${feeIndex} Cost`] = `$${formatNumber(fee.cost ?? 0, 2)}`;
          feeIndex++;
        });

        unitFlatFees.forEach((fee) => {
          // line_items come from generateBill API (snake_case)
          if (fee.unit_id === unit.id) {
            baseRow[`Fee ${feeIndex} Name`] = fee.description || 'Unit-Specific Fee';
            baseRow[`Fee ${feeIndex} Type`] = 'UNIT_FLAT';
            baseRow[`Fee ${feeIndex} Cost`] = `$${formatNumber(fee.cost ?? 0, 2)}`;
            feeIndex++;
          }
        });
      }

      if (stripeConnect) {
        baseRow['Status'] = unit.stripeLastStatus;
        baseRow['Invoice URL'] = unit.stripeInvoicePdfUrl;
      }

      return baseRow;
    };

    // Invoiced row for a unit: sources costs from the persisted custom invoice instead of
    // recomputing them, so the export matches what the customer's PDF actually shows.
    // One row per invoice, mirroring the RenderTable row shape for multi-tenant units
    // (each tenant's own invoice gets its own row rather than a single per-unit total).
    // Returns the row plus the persisted (or fallback) total in dollars so the caller can
    // compare the invoiced total against the live-computed equivalent for the rounding note.
    const buildPersistedRow = (unit: any, inv: any): { row: any; totalDollars: number } => {
      // Un-prorated unit total: what the live-compute path would have shown for this unit.
      // Used both as the fallback total and as the denominator for the proration ratio that
      // scales the individual fee columns when only the aggregate amount is persisted.
      const computedInvoiceAmount =
        (unit.unit_cost_meter || 0) + (unit.unit_estimated_cost || 0) + (unit.unit_cost || 0);

      const persistedCents = customInvoiceAmountCents.get(inv.id);
      const persistedDollars = persistedCents !== undefined ? persistedCents / 100 : undefined;
      const resolvedTotal = persistedDollars ?? computedInvoiceAmount;
      const prorationRatio =
        computedInvoiceAmount > 0 && resolvedTotal !== computedInvoiceAmount
          ? resolvedTotal / computedInvoiceAmount
          : 1;

      const persistedBreakdown = customInvoiceBreakdown.get(inv.id);
      const persistedLineItems: CustomInvoiceLineItem[] | null =
        customInvoiceLineItems.get(inv.id) ?? null;

      const rowEstimatedCost = persistedLineItems
        ? persistedLineItems
            .filter((li) => li.category === 'estimated_usage')
            .reduce((sum, li) => sum + li.amount_cents, 0) / 100
        : (unit.unit_estimated_cost ?? 0) * prorationRatio;
      // The persisted usage cents include estimated usage (the PDF groups them into one
      // category), so back estimated out of it here, matching the same correction the row
      // tooltip applies (getBreakdownItems), so Water + Estimated don't double-count.
      // No floor: a negative metered value (reversed meter on a legacy invoice)
      // stays visible so the row's lines still sum to its total
      const rowWaterCost = persistedBreakdown
        ? persistedBreakdown.usageCents / 100 - rowEstimatedCost
        : (unit.unit_cost_meter || 0) * prorationRatio;

      // Prefer the exact persisted line item for a given fee (matched by category and the
      // same description used on the bill's fee catalog, which is what gets rendered
      // verbatim onto the PDF). Falls back to scaling the catalog amount by the invoice's
      // overall proration ratio when there's no line-item snapshot for this invoice, or no
      // line matches (legacy invoices rendered before the snapshot table existed).
      const feeCostForCategory = (
        computedAmount: number,
        category: CustomInvoiceLineItem['category'],
        description: string,
      ): number => {
        const match = persistedLineItems?.find(
          (li) => li.category === category && li.description === description,
        );
        return match ? match.amount_cents / 100 : computedAmount * prorationRatio;
      };

      const subscriber = inv.subscriberId ? subscriberById.get(inv.subscriberId) : undefined;

      const baseRow: any = {
        'Unit Name': unit.name,
        Tenant: subscriber ? `${subscriber.first_name} ${subscriber.last_name}` : '—',
        'Structure Name': bill.structure_name,
        Volume: formatNumber(unit.unit_total_volume, 3),
        'Volume Units': bill.volume_units,
        'Effective Rate': formatNumber(bill.effective_rate, 2),
        'Total Cost': `$${formatNumber(resolvedTotal, 2)}`,
        'Cost Units': bill.cost_units,
        'Water Usage Cost': `$${formatNumber(rowWaterCost, 2)}`,
      };
      if (csvHasEstimates) {
        baseRow['Estimated Volume'] = formatNumber(unit.unit_estimated_volume ?? 0, 3);
        baseRow['Estimated Usage Cost'] = `$${formatNumber(rowEstimatedCost, 2)}`;
      }

      if (hasAnyFees) {
        let feeIndex = 1;

        splitFees.forEach((fee) => {
          const description = fee.description || 'Split Fee';
          const computedAmount = (fee.cost ?? 0) / billableUnitsCount;
          baseRow[`Fee ${feeIndex} Name`] = description;
          baseRow[`Fee ${feeIndex} Type`] = 'SPLIT';
          baseRow[`Fee ${feeIndex} Cost`] = `$${formatNumber(
            feeCostForCategory(computedAmount, 'split_fee', description),
            2,
          )}`;
          feeIndex++;
        });

        flatFees.forEach((fee) => {
          const description = fee.description || 'Flat Fee';
          const computedAmount = fee.cost ?? 0;
          baseRow[`Fee ${feeIndex} Name`] = description;
          baseRow[`Fee ${feeIndex} Type`] = 'FLAT';
          baseRow[`Fee ${feeIndex} Cost`] = `$${formatNumber(
            feeCostForCategory(computedAmount, 'flat_fee', description),
            2,
          )}`;
          feeIndex++;
        });

        unitFlatFees.forEach((fee) => {
          if (fee.unit_id === unit.id) {
            const description = fee.description || 'Unit-Specific Fee';
            const computedAmount = fee.cost ?? 0;
            baseRow[`Fee ${feeIndex} Name`] = description;
            baseRow[`Fee ${feeIndex} Type`] = 'UNIT_FLAT';
            baseRow[`Fee ${feeIndex} Cost`] = `$${formatNumber(
              feeCostForCategory(computedAmount, 'unit_flat_fee', description),
              2,
            )}`;
            feeIndex++;
          }
        });
      }

      if (stripeConnect) {
        baseRow['Status'] = inv.stripeLastStatus;
        baseRow['Invoice URL'] = inv.stripeInvoicePdfUrl;
      }

      return { row: baseRow, totalDollars: resolvedTotal };
    };

    const rows: any[] = [];
    // Per-unit sum of persisted invoice totals minus the unit's live-computed
    // full-period total. Non-zero for legitimate reasons (vacancy windows and
    // owner-absorbed shares, mid-period tenant changes, invoices generated under
    // an earlier rounding scheme); drives the trailing informational note.
    let invoicedRoundingDiffDollars = 0;

    sortedUnits.forEach((unit) => {
      const unitInvoices = hasCustomInvoicesForBill
        ? localInvoices.filter((inv: any) => inv?.unitId === unit.id)
        : [];

      // All-or-nothing per unit, matching resolveUnitPmsAmount (pmsExport.ts):
      // buildPersistedRow falls back to the FULL un-prorated unit total for an
      // invoice missing amount_due_cents, so emitting persisted rows for a unit
      // where only some invoices carry a persisted amount would either
      // double-count the unit or silently drop a sibling invoice's share.
      // Persisted and live-computed amounts never mix within a unit.
      const everyInvoiceHasPersistedAmount =
        unitInvoices.length > 0 &&
        unitInvoices.every((inv: any) => customInvoiceAmountCents.has(inv.id));

      if (everyInvoiceHasPersistedAmount) {
        let unitPersistedDollars = 0;
        unitInvoices.forEach((inv: any) => {
          const { row, totalDollars } = buildPersistedRow(unit, inv);
          rows.push(row);
          unitPersistedDollars += totalDollars;
        });
        // Compare the unit's invoices against its full-period computation ONCE per
        // unit. Subtracting the computed total per invoice (as this originally did)
        // overstates the difference for any unit with multiple invoices, since the
        // full-unit total gets counted once per tenant.
        const computedInvoiceAmount =
          (unit.unit_cost_meter || 0) + (unit.unit_estimated_cost || 0) + (unit.unit_cost || 0);
        invoicedRoundingDiffDollars += unitPersistedDollars - computedInvoiceAmount;
        return;
      }

      rows.push(buildComputedRow(unit));
    });

    // Invoice totals can legitimately differ from a fresh full-period computation:
    // vacancy windows and owner-absorbed shares stay unbilled, mid-period tenant
    // changes split a unit across invoices, and legacy invoices may predate a billing
    // update. Surface an informational note rather than silently showing numbers that
    // don't add up; when the totals match (the common case), no note is added.
    if (hasCustomInvoicesForBill && Math.abs(invoicedRoundingDiffDollars) > 0.005) {
      rows.push({
        'Unit Name': 'Note',
        Notes: `The invoice amounts in this export differ from a fresh full-period computation by $${formatNumber(
          Math.abs(invoicedRoundingDiffDollars),
          2,
        )}. Amounts above reflect the invoices as issued. Differences are expected when units were vacant for part of the period, when tenants changed mid-period, or for invoices generated before a billing update.`,
      });
    }

    // 5) if not on Stripe, drop Status & Invoice URL
    const finalRows = stripeConnect
      ? rows
      : rows.map(({ Status: _Status, 'Invoice URL': _InvoiceURL, ...keep }) => keep);

    const csv = await convertArrayToCsvString(finalRows);
    saveAs(
      new Blob([csv], { type: 'text/plain;charset=utf-8' }),
      `${title.replace(/ /g, '_')}.csv`,
    );
  }, [
    bill,
    localInvoices,
    stripeConnect,
    title,
    customInvoiceAmountCents,
    customInvoiceBreakdown,
    customInvoiceLineItems,
    subscriberById,
  ]);

  useEffect(() => {
    const handleAction = async () => {
      if (autoDownload === 'pdf') return toPDF();

      if (!alredyCSV && open && autoDownload === 'csv' && bill && updatedBill) {
        setAlredyCSV(true);
        try {
          await handleCsvDownload();
          await trackEvent('Bill Downloaded', eventDetails);
          onClose();
        } catch (error) {
          console.error('Error during CSV download:', error);
        }
      }
    };

    handleAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    autoDownload,
    alredyCSV,
    handleCsvDownload,
    trackEvent,
    eventDetails,
    onClose,
    bill,
    updatedBill,
  ]);

  const handleSendReport = async () => {
    setLoading(true);
    try {
      await sendTenantReport(bill.billing_id);
      await trackEvent('Subscriber Usage Report Send', {
        PropertyID: eventDetails.PropertyID,
        PropertyName: eventDetails.PropertyName,
        BillID: eventDetails.BillID,
      });
      onClose();
    } catch {
      // Failure already toasted by the api layer; keep the dialog open so the
      // user can retry instead of silently closing as if it sent
    } finally {
      setLoading(false);
    }
  };

  const handleSendBill = async () => {
    setLoading(true);
    try {
      // If "Save as default" is checked, update property settings first
      if (batchSaveAsDefault && onPaymentSettingsUpdate) {
        await onPaymentSettingsUpdate(
          batchPayOnlineUrl.trim() || null,
          batchPaymentInstructions.trim() || null,
          batchDueDateDays,
        );
        toast.success('Payment settings saved as default!', { closeButton: true });
      }

      await trackEvent('Invoice Sent', {
        PropertyName: bill.structure_name,
        PropertyID: bill.structure_id,
        StartDate: convertTimestampToTimeZone(bill.start_time, timeZone),
        EndDate: convertTimestampToTimeZone(bill.end_time, timeZone),
      });

      // 1) Send Stripe invoices
      await sendBills(bill.billing_id, batchDueDateDays);
      // Update local timestamp so UI immediately reflects bills have been sent
      setLocalBillsSentTimestamp(Math.floor(Date.now() / 1000));
      toast.success('Stripe invoices sent to tenants!', { closeButton: true });

      // 2) Generate SSW invoice PDFs with the same settings
      const options: GenerateBatchCustomInvoicesRequest = {
        due_date_days: batchDueDateDays,
      };
      if (batchPayOnlineUrl.trim()) {
        options.pay_online_url = batchPayOnlineUrl.trim();
      }
      if (batchPaymentInstructions.trim()) {
        options.payment_instructions = batchPaymentInstructions.trim();
      }
      if (Object.keys(batchUnitGraphModeOverrides).length > 0) {
        options.unit_graph_mode_overrides = batchUnitGraphModeOverrides;
        if (batchSaveGraphModeOverrides) {
          options.save_unit_graph_mode_overrides = true;
        }
      }

      const result = await generateBatchCustomInvoices(bill.billing_id, options);
      setBatchCustomInvoices(result.custom_invoices);
      // Mark these invoices as pending so individual generate buttons are disabled
      const pendingIds = new Set(result.custom_invoices.map((ci) => ci.billing_invoice_id));
      setPendingCustomInvoiceIds(pendingIds);
      // Clear Stripe sending state and start batch generation together to avoid showing form
      setIsSendingStripeInvoices(false);
      setBatchGenerationStarted(true);

      await trackEvent('Batch Custom Invoices Generated', {
        PropertyName: bill.structure_name,
        PropertyID: bill.structure_id,
        BillID: bill.billing_id,
        TotalCount: result.total_queued,
      });

      toast.success(`Invoices sent! ${result.total_queued} SSW invoice PDFs are being generated.`, {
        closeButton: true,
      });
    } catch (error) {
      console.error('Failed to send invoices:', error);
      toast.error('Failed to send invoices. Please try again.', { closeButton: true });
      setIsSendingStripeInvoices(false);
    } finally {
      setLoading(false);
    }
  };

  // Handler for opening Stripe send invoices dialog
  const handleOpenSendInvoicesDialog = () => {
    setBatchPayOnlineUrl(payOnlineUrl || '');
    setBatchPaymentInstructions(paymentInstructions || '');
    setBatchDueDateDays(dueDateDays ?? 21);
    setOpenConfirmSendInvoices(true);
  };

  // Handler for opening batch generate dialog
  const handleOpenBatchGenerateDialog = () => {
    setBatchPayOnlineUrl(payOnlineUrl || '');
    setBatchPaymentInstructions(paymentInstructions || '');
    setBatchDueDateDays(dueDateDays ?? 21);
    setBatchSaveAsDefault(false);
    setBatchUrlTouched(false);
    setBatchInstructionsTouched(false);
    setBatchGenerationStarted(false);
    setBatchCustomInvoices([]);
    setBatchGenerationProgress(null);
    generationCompletedRef.current = false;
    setOpenBatchGenerateDialog(true);
  };

  // Handler for batch custom invoice generation
  const handleBatchGenerate = async () => {
    setIsBatchGenerating(true);

    try {
      // If "Save as default" is checked, update property settings first
      if (batchSaveAsDefault && onPaymentSettingsUpdate) {
        await onPaymentSettingsUpdate(
          batchPayOnlineUrl.trim() || null,
          batchPaymentInstructions.trim() || null,
          batchDueDateDays,
        );
        toast.success('Payment settings saved as default!', { closeButton: true });
      }

      // Prepare options
      // If user touched a field, always send it (even if empty) to override defaults
      // If user didn't touch, don't send it so backend can use property defaults
      const options: GenerateBatchCustomInvoicesRequest = {};
      if (batchUrlTouched || batchPayOnlineUrl.trim()) {
        options.pay_online_url = batchPayOnlineUrl.trim();
      }
      if (batchInstructionsTouched || batchPaymentInstructions.trim()) {
        options.payment_instructions = batchPaymentInstructions.trim();
      }
      // Only pass due_date_days for non-Stripe users
      if (!stripeConnect) {
        options.due_date_days = batchDueDateDays;
      }
      // Per-unit usage graph mode overrides for this batch (only if any units differ from default)
      if (Object.keys(batchUnitGraphModeOverrides).length > 0) {
        options.unit_graph_mode_overrides = batchUnitGraphModeOverrides;
        if (batchSaveGraphModeOverrides) {
          options.save_unit_graph_mode_overrides = true;
        }
      }
      // Regen mode: tell the backend to wipe existing billing invoices for this
      // billing (cascades custom invoices) and rebuild them from current occupancy.
      if (isRegenerating) {
        options.regenerate = true;
      }

      // Trigger batch generation
      const result = await generateBatchCustomInvoices(bill.billing_id, options);
      setBatchCustomInvoices(result.custom_invoices);
      setBatchGenerationStarted(true);

      await trackEvent('Batch Custom Invoices Generated', {
        PropertyName: bill.structure_name,
        PropertyID: bill.structure_id,
        BillID: bill.billing_id,
        TotalCount: result.total_queued,
      });

      toast.success(
        `Started generating ${result.total_queued} SSW invoice PDFs. This may take a moment.`,
        {
          closeButton: true,
        },
      );
    } catch (error) {
      console.error('Failed to generate batch custom invoices:', error);
      // Error toast is handled in API function
    } finally {
      setIsBatchGenerating(false);
    }
  };

  // Handler for closing batch generate dialog.
  //
  // IMPORTANT: do NOT reset `batchGenerationStarted`, `batchCustomInvoices`,
  // `batchGenerationProgress`, `isPolling`, or `generationCompletedRef` here.
  // Those state values drive the background polling effect — clearing them
  // on close kills the interval mid-generation and prevents the eventual
  // `onRefreshBills()` call. Let polling run to completion in the background;
  // the polling effect itself resets that state when it finishes (see the
  // completion branch in the polling useEffect).
  const handleCloseBatchGenerateDialog = () => {
    setOpenBatchGenerateDialog(false);
    setIsSendingStripeInvoices(false);
    setBatchUnitGraphModeOverrides({});
    setBatchSaveGraphModeOverrides(false);
    setIsRegenerating(false);
  };

  const handleOpenRegenerateDialog = () => {
    setIsRegenerating(true);
    handleOpenBatchGenerateDialog();
  };

  // Helpers for PDF filenames
  const sanitize = (s: string) => s.replace(/[^a-zA-Z0-9]/g, '_');
  const formatDateForFilename = (timestamp: number) => {
    const d = moment.unix(timestamp);
    if (timeZone) d.tz(timeZone);
    return d.format('MM-DD-YYYY');
  };

  // Handler for downloading selected invoices - merges PDFs using pdf-lib
  const handleDownloadSelected = async () => {
    if (selectedInvoiceIds.size === 0) return;

    setLoading(true);
    try {
      // Get download URLs for selected invoices
      const result = await getCustomInvoiceDownloadUrls(
        bill.billing_id,
        Array.from(selectedInvoiceIds),
      );

      // Check if all selected invoices are ready
      if (result.not_ready_count > 0) {
        toast.warning(
          `${result.not_ready_count} invoice(s) are still being generated. Please wait and try again.`,
          { closeButton: true },
        );
        setLoading(false);
        return;
      }

      const readyUrls = result.custom_invoices.filter((d) => d.download_url);

      // Sort by unit name alphanumerically so merged PDF pages are in order
      const unitMap = new Map(bill.units.map((u) => [u.id, u.name]));
      readyUrls.sort((a, b) => {
        const invA = invoices.find((inv) => inv.id === a.billing_invoice_id);
        const invB = invoices.find((inv) => inv.id === b.billing_invoice_id);
        const nameA = (invA ? unitMap.get(invA.unitId) : '') || '';
        const nameB = (invB ? unitMap.get(invB.unitId) : '') || '';
        return naturalCompare(nameA, nameB);
      });

      if (readyUrls.length === 0) {
        toast.error('No invoices are ready for download.', { closeButton: true });
        setLoading(false);
        return;
      }

      if (readyUrls.length === 1) {
        // Single PDF - download with descriptive filename
        const matchingInvoice = invoices.find((inv) => inv.id === readyUrls[0].billing_invoice_id);
        const matchingUnit = bill.units.find((u) => u.id === matchingInvoice?.unitId);
        const unitName = matchingUnit?.name || 'Invoice';

        const response = await fetch(readyUrls[0].download_url!);
        const pdfBytes = await response.arrayBuffer();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);

        const startDate = formatDateForFilename(bill.start_time);
        const endDate = formatDateForFilename(bill.end_time);
        const filename = `${sanitize(orgName)}_${sanitize(bill.structure_name)}_${sanitize(
          unitName,
        )}_${startDate}_to_${endDate}.pdf`;

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } else {
        // Multiple PDFs - merge using pdf-lib
        toast.info(`Merging ${readyUrls.length} invoices into a single PDF...`, {
          closeButton: true,
        });

        // Helper: download each PDF as its own file. Used as a fallback when pdf-lib
        // can't merge (e.g. it raises "unsupported color function 'color'" on PDF content
        // streams ReportLab emits that pdf-lib 1.17 doesn't recognize). Browsers usually
        // require a user gesture per download, so we trigger them in sequence with small
        // delays; some browsers will prompt the user to allow multiple downloads.
        const downloadIndividually = async () => {
          for (const urlInfo of readyUrls) {
            const link = document.createElement('a');
            link.href = urlInfo.download_url!;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            await new Promise((r) => setTimeout(r, 250));
          }
        };

        try {
          // Create a new PDF document to merge into
          const mergedPdf = await PDFDocument.create();

          let perPdfFailureCount = 0;
          // Fetch and merge each PDF
          for (const urlInfo of readyUrls) {
            try {
              const response = await fetch(urlInfo.download_url!);
              const pdfBytes = await response.arrayBuffer();
              const pdfDoc = await PDFDocument.load(pdfBytes);
              const copiedPages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
              copiedPages.forEach((page) => mergedPdf.addPage(page));
            } catch (err) {
              perPdfFailureCount += 1;
              console.error(
                `Failed to fetch/merge PDF for invoice ${urlInfo.billing_invoice_id}:`,
                err,
              );
              // Continue with other PDFs even if one fails
            }
          }

          // If pdf-lib failed to copy any pages (typically the "unsupported color function"
          // error on every PDF), the merged output would be empty. Fall back to triggering
          // individual downloads so the user still gets the files.
          if (mergedPdf.getPageCount() === 0) {
            toast.error(
              'Could not merge PDFs into one file. Opening each invoice separately instead.',
              { closeButton: true },
            );
            await downloadIndividually();
            return;
          }

          // Save the merged PDF
          const mergedPdfBytes = await mergedPdf.save();

          // Create a blob and download link
          const blob = new Blob([mergedPdfBytes as BlobPart], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);

          // Create download filename with date range
          const startDate = formatDateForFilename(bill.start_time);
          const endDate = formatDateForFilename(bill.end_time);
          const filename = `${sanitize(orgName)}_${sanitize(
            bill.structure_name,
          )}_${startDate}_to_${endDate}.pdf`;

          // Trigger download
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up the object URL
          URL.revokeObjectURL(url);

          if (perPdfFailureCount > 0) {
            toast.warning(
              `Merged ${readyUrls.length - perPdfFailureCount} of ${
                readyUrls.length
              } invoices; ${perPdfFailureCount} could not be merged. Open those individually.`,
              { closeButton: true },
            );
          } else {
            toast.success(`Successfully merged ${readyUrls.length} invoices!`, {
              closeButton: true,
            });
          }
        } catch (mergeError) {
          console.error('Failed to merge PDFs:', mergeError);
          toast.error(
            'Could not merge PDFs into one file. Opening each invoice separately instead.',
            { closeButton: true },
          );
          await downloadIndividually();
        }
      }

      await trackEvent('Batch Custom Invoices Downloaded', {
        PropertyName: bill.structure_name,
        PropertyID: bill.structure_id,
        BillID: bill.billing_id,
        SelectedCount: selectedInvoiceIds.size,
        ReadyCount: result.ready_count,
      });
    } catch (error) {
      console.error('Failed to download selected invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvoiceEmails = async () => {
    setIsSendingEmails(true);
    try {
      const result = await sendCustomInvoiceEmails(bill.billing_id, Array.from(selectedInvoiceIds));
      setOpenSendEmailDialog(false);

      // Optimistic update: immediately populate emailSentMap so column + warning update instantly
      const now = Math.floor(Date.now() / 1000);
      setEmailSentMap((prev) => {
        const updated = new Map(prev);
        selectedInvoiceIds.forEach((id) => updated.set(id, now));
        return updated;
      });

      if (result.skipped > 0) {
        toast.warning(`${result.total_queued} email(s) queued. ${result.skipped} skipped.`, {
          closeButton: true,
        });
      } else {
        toast.success(`${result.total_queued} invoice email(s) queued for sending!`, {
          closeButton: true,
        });
      }

      // Refetch to get updated email_sent_at from DB
      if (bill?.billing_id) {
        const allInvoiceIds = invoices.filter((inv) => inv.id).map((inv) => inv.id);
        if (allInvoiceIds.length > 0) {
          const refreshResult = await getCustomInvoiceDownloadUrls(bill.billing_id, allInvoiceIds);
          const emailSent = new Map<string, number>();
          refreshResult.custom_invoices.forEach((ci) => {
            if (ci.email_sent_at) {
              emailSent.set(ci.billing_invoice_id, ci.email_sent_at);
            }
          });
          setEmailSentMap(emailSent);
        }
      }
    } catch (error) {
      // API function already shows toast error
    } finally {
      setIsSendingEmails(false);
    }
  };

  // 1) one single source of truth for invoice‐status counts
  const statusCount = React.useMemo<IStatusCount>(() => {
    return localInvoices.reduce((acc, inv) => {
      const s = inv.stripeLastStatus || 'unknown';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {} as IStatusCount);
  }, [localInvoices]);

  // 2) total = number of prorated‐segments = localInvoices.length
  const totalInvoices = localInvoices.length;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        scroll="body"
        aria-labelledby="form-dialog-title"
        maxWidth={isConfirmReport ? 'sm' : 'lg'}
        fullWidth
        classes={{
          paper: classes.paper,
        }}
        sx={
          isConfirmReport
            ? {
                '& .MuiDialog-paper': {
                  minHeight: 'auto',
                  maxHeight: 'calc(100% - 64px)',
                },
              }
            : {}
        }
      >
        {/* === FINAL‐REVIEW HEADER === */}
        {!isConfirmReport && (
          <Stack
            direction={'column'}
            alignItems={'center'}
            gap={1}
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: (theme) => theme.palette.background.paper,
              zIndex: 10,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            {/* Title + close button */}
            <DialogTitle id="form-dialog-title" fontSize={'24px'} fontWeight={700}>
              Add Water Utility Bill
              <IconButton
                onClick={onClose}
                size="small"
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            {/* 3-segment blue progress bar */}
            {/* Billing progress bar*/}
            {stripeConnect && (
              <Box width="80%" mt={-1} mb={-1}>
                <Stack direction="row" spacing={1}>
                  {[0, 1, 2].map((step) => (
                    <Box
                      key={step}
                      flex={1}
                      height={10}
                      borderRadius={3}
                      bgcolor={step <= 2 ? 'primary.main' : '#E0E0E0'}
                    />
                  ))}
                </Stack>
              </Box>
            )}
            {/* Billing w/o Stripe progress bar */}
            {!stripeConnect && (
              <Box width="80%" mt={-1} mb={-1}>
                <Stack direction="row" spacing={1}>
                  {[0, 1, 2].map((step) => (
                    <Box
                      key={step}
                      flex={1}
                      height={10}
                      borderRadius={3}
                      bgcolor={step <= 2 ? 'primary.main' : '#E0E0E0'}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Subtitle + blurb */}
            {stripeConnect && (
              <Typography variant="subtitle1" fontWeight={500} sx={{ mt: 2 }}>
                Step 3: Final Review
              </Typography>
            )}
            {!stripeConnect && (
              <Typography variant="subtitle1" fontWeight={500} sx={{ mt: 2 }}>
                Step 3: Final Review
              </Typography>
            )}
            <Typography variant="body2" textAlign="center">
              Review how the water utility bill has been distributed across tenants.
            </Typography>
            <Divider sx={{ borderWidth: 1, borderColor: 'grey', width: '100%', mt: 1 }} />
          </Stack>
        )}
        <DialogContent
          ref={targetRef}
          sx={
            isConfirmReport
              ? {
                  pt: 3,
                  pb: 1,
                  minHeight: 'auto',
                  '&.MuiDialogContent-root': {
                    minHeight: 'auto',
                    flex: 'initial',
                  },
                }
              : {}
          }
        >
          {!isConfirmReport && (
            <>
              {/* Pre-flight hint only: since v2.0.4 the server hard-blocks generation on
                  negative unit usage (validate_no_negative_unit_usage) and the 4xx message
                  is toasted verbatim; this banner just warns before the user gets that far. */}
              {(bill.units || []).some(
                (u: any) => (u.unit_total_volume ?? 0) < 0 || (u.unit_cost_meter ?? 0) < 0,
              ) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  One or more units on this bill show negative water usage, which can produce
                  negative invoice totals. Please contact Support before generating or sending
                  invoices.
                </Alert>
              )}
              {staleOccupancyUnits.length > 0 && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  action={
                    <SWLoadingButtonOutlined
                      size="small"
                      onClick={handleOpenRegenerateDialog}
                      loading={isBatchGenerating}
                    >
                      Regenerate Invoices
                    </SWLoadingButtonOutlined>
                  }
                >
                  {(() => {
                    const occupancyNames = Array.from(
                      new Set(
                        staleOccupancyUnits
                          .filter((u) => u.reason === 'occupancy')
                          .map((u) => u.unitName),
                      ),
                    );
                    const estimatedNames = Array.from(
                      new Set(
                        staleOccupancyUnits
                          .filter((u) => u.reason === 'estimated')
                          .map((u) => u.unitName),
                      ),
                    );
                    const meteredNames = Array.from(
                      new Set(
                        staleOccupancyUnits
                          .filter((u) => u.reason === 'metered')
                          .map((u) => u.unitName),
                      ),
                    );
                    return (
                      <>
                        {occupancyNames.length > 0 && (
                          <>
                            The property's unit roster or tenant occupancy has changed since these
                            invoices were generated (
                            {occupancyNames.length === 1 ? 'unit' : 'units'}{' '}
                            <strong>{occupancyNames.join(', ')}</strong>).{' '}
                          </>
                        )}
                        {estimatedNames.length > 0 && (
                          <>
                            Estimated usage was added or changed after these invoices were generated
                            ({estimatedNames.length === 1 ? 'unit' : 'units'}{' '}
                            <strong>{estimatedNames.join(', ')}</strong>).{' '}
                          </>
                        )}
                        {meteredNames.length > 0 && (
                          <>
                            Meter data arrived or changed after these invoices were generated (
                            {meteredNames.length === 1 ? 'unit' : 'units'}{' '}
                            <strong>{meteredNames.join(', ')}</strong>).{' '}
                          </>
                        )}
                        Regenerating will delete the old invoices and create new ones matching the
                        current state.
                      </>
                    );
                  })()}
                </Alert>
              )}
              {!stripeConnect &&
                localInvoices.length === 0 &&
                partialOccupancyUnitNames.length > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {partialOccupancyUnitNames.length === 1
                      ? 'A tenant in unit '
                      : 'Tenants in units '}
                    <strong>{partialOccupancyUnitNames.join(', ')}</strong> occupied their unit for
                    only part of this billing period. Each invoice bills just the water used during
                    that tenant's occupancy, so invoice totals will be less than the Total Cost. The
                    remainder is the owner's share.
                  </Alert>
                )}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="stretch"
                spacing={2}
              >
                {!isPaymentReport && (
                  <Card
                    variant="outlined"
                    sx={
                      effectiveBillsSentTimestamp
                        ? {
                            flexGrow: 1,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            borderRadius: '14px',
                          }
                        : {
                            flexGrow: 1,
                            borderRadius: '14px',
                          }
                    }
                  >
                    <Box margin={1}>
                      <Stack alignItems="center">
                        <Typography textAlign="center" fontWeight={400} fontSize={'14px'}>
                          Billing Period
                        </Typography>
                        <Typography textAlign="center" fontWeight={700} fontSize={'24px'}>
                          {dayjs.unix(bill.start_time).tz(tz).format('MMM D')} -{' '}
                          {dayjs.unix(bill.end_time).tz(tz).format('MMM D')}
                        </Typography>
                      </Stack>
                    </Box>
                  </Card>
                )}
                {!isPaymentReport && (
                  <Card
                    variant="outlined"
                    sx={
                      effectiveBillsSentTimestamp
                        ? {
                            flexGrow: 1,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            borderRadius: '14px',
                          }
                        : {
                            flexGrow: 1,
                            borderRadius: '14px',
                          }
                    }
                  >
                    <Box margin={1}>
                      <Stack alignItems="center">
                        <Typography textAlign="center" fontWeight={400} fontSize={'14px'}>
                          Total Volume
                        </Typography>
                        <Typography textAlign="center" fontWeight={700} fontSize={'24px'}>
                          {bill.volume}
                        </Typography>
                      </Stack>
                    </Box>
                  </Card>
                )}
                {!isPaymentReport && (
                  <Card
                    variant="outlined"
                    sx={
                      effectiveBillsSentTimestamp
                        ? {
                            flexGrow: 1,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            borderRadius: '14px',
                          }
                        : {
                            flexGrow: 1,
                            borderRadius: '14px',
                          }
                    }
                  >
                    <Box margin={1}>
                      <Stack alignItems="center">
                        <Typography textAlign="center" fontWeight={400} fontSize={'14px'}>
                          Total Cost
                        </Typography>
                        <Typography textAlign="center" fontWeight={700} fontSize={'24px'}>
                          $
                          {formatNumber(
                            // use updatedBill if you have it, otherwise show bill.cost
                            updatedBill?.cost ?? bill.cost,
                            2,
                          )}
                        </Typography>
                        {/* Occupancy reconciliation for the as-issued invoices. Two
                            statements, deliberately NOT an "A + B = Total Cost" sum:
                            the owner share covers only invoiced units, while the card
                            total also includes units that never got an invoice. Null
                            whenever the only gaps are cent-level rounding drift. */}
                        {ownerShareSummary && (
                          <>
                            <Typography textAlign="center" fontSize="12px" color="text.secondary">
                              Invoices as issued: $
                              {formatNumber(ownerShareSummary.invoicedTotalDollars, 2)}
                            </Typography>
                            <Typography textAlign="center" fontSize="12px" color="text.secondary">
                              Owner share (partial occupancy): $
                              {formatNumber(ownerShareSummary.ownerShareDollars, 2)}
                            </Typography>
                          </>
                        )}
                      </Stack>
                    </Box>
                  </Card>
                )}
                {/* BOOLEAN DEFINITIONS: */}
                {/* needSendBillButton => sending invoices...? not needed...? */}
                {/* needReportButton => clicking "Send Usage to Tenants"*/}
                {/* needStatus={needSendBillButton || needReportButton ? false : true} => need the Status Column */}
                {/* isConfirmReport => confirm sending usage to tenants */}
                {/* !!effectiveBillsSentTimestamp => when Invoices have been sent; */}
                {/* isPaymentReport => when "View Payment Report" is clicked... that's been deprecated... not needed...? */}
                {/* billingEnabled => billing w/ & w/o stripe */}
                {!isPaymentReport &&
                  billingEnabled &&
                  !!effectiveBillsSentTimestamp &&
                  !needReportButton && (
                    <Card variant="outlined" sx={{ flexGrow: 1, borderRadius: '14px' }}>
                      <Box margin={1}>
                        <Stack alignItems="center">
                          <Typography textAlign="center" fontWeight={400} fontSize={'14px'}>
                            Total Tenant Invoices
                          </Typography>
                          <Typography fontSize="24px" fontWeight={700}>
                            {totalInvoices}
                          </Typography>
                        </Stack>
                        <Stack
                          justifyContent="center"
                          alignItems="center"
                          direction="row"
                          spacing={1}
                          mt={1}
                        >
                          <Chip
                            label={`Open: ${statusCount.open ?? 0}`}
                            variant="outlined"
                            size="small"
                            sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                          />
                          <Chip
                            label={`Paid: ${statusCount.paid ?? 0}`}
                            variant="outlined"
                            size="small"
                            color="success"
                          />
                          <Chip
                            label={`Uncollectible: ${statusCount.uncollectible ?? 0}`}
                            variant="outlined"
                            size="small"
                            color="error"
                          />
                        </Stack>
                      </Box>
                    </Card>
                  )}
              </Stack>
            </>
          )}
          {!isConfirmReport && (
            <RenderTable
              bill={updatedBill ?? (bill as IUpdatedBillingData)}
              invoices={localInvoices}
              subscriberById={subscriberById}
              billingContactWindowsByUnit={billingContactWindowsByUnit}
              autoDownload={autoDownload}
              title={title}
              onClose={onClose}
              eventDetails={eventDetails}
              timeZone={timeZone}
              _needStatus={needSendBillButton || needReportButton ? false : true}
              needSendBillButton={needSendBillButton}
              billsSentTimestamp={effectiveBillsSentTimestamp}
              stripeConnect={stripeConnect}
              billingEnabled={billingEnabled}
              onInvoicePdfGenerated={handleInvoicePdfGenerated}
              payOnlineUrl={payOnlineUrl}
              paymentInstructions={paymentInstructions}
              dueDateDays={dueDateDays}
              onPaymentSettingsUpdate={onPaymentSettingsUpdate}
              selectedInvoiceIds={selectedInvoiceIds}
              onSelectionChange={setSelectedInvoiceIds}
              showSelectionCheckboxes={localInvoices.length > 0 && !stripeConnect}
              pendingCustomInvoiceIds={pendingCustomInvoiceIds}
              customInvoiceDueDates={customInvoiceDueDates}
              emailSentMap={emailSentMap}
              customInvoicePaymentInfoMap={customInvoicePaymentInfoMap}
              customInvoiceAmountCents={customInvoiceAmountCents}
              customInvoiceBreakdown={customInvoiceBreakdown}
              customInvoiceLineItems={customInvoiceLineItems}
              onDownloadSelected={
                localInvoices.length > 0 && !stripeConnect ? handleDownloadSelected : undefined
              }
              onSendEmail={
                localInvoices.length > 0 && !stripeConnect
                  ? () => setOpenSendEmailDialog(true)
                  : undefined
              }
              isSendingEmails={isSendingEmails}
            />
          )}
          {isConfirmReport && (
            <RenderConfirmReport
              propertyName={bill.structure_name}
              // Match the format used by the rest of the app's address display
              // (Structures/View/components/Address.tsx, Units/View/Address.tsx):
              //   "Street Suite, City State Zip"
              // The previous string put state before city and dropped street2.
              address={`${structureAddress.street}${
                structureAddress.street2 ? ` ${structureAddress.street2}` : ''
              }, ${structureAddress.city} ${structureAddress.state} ${structureAddress.zip}`}
              dateRange={`${formattedStartDate} - ${formattedEndDate}`}
            />
          )}
        </DialogContent>
        <DialogActions
          classes={{ root: classes.dialogActionsRoot }}
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: (theme) => theme.palette.background.paper,
            zIndex: 10,
            justifyContent: 'space-between',
            px: 2,
            py: 2,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* LEFT CLOSE; !confirmReport => !confirm send usage */}
          {!isConfirmReport && (
            <SWLoadingButtonOutlined onClick={onClose}>Close</SWLoadingButtonOutlined>
          )}
          {/* Right: Download + Send; !confirmReport => !confirm send usage*/}
          {!isConfirmReport && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <>
                <SWLoadingButton
                  aria-controls={isDownloadMenuOpen ? 'download-menu' : undefined}
                  aria-haspopup="true"
                  onClick={handleDownloadMenuOpen}
                  endIcon={<ArrowDropDownIcon />}
                >
                  Download Data
                </SWLoadingButton>
                <Menu
                  id="download-menu"
                  anchorEl={downloadAnchorEl}
                  open={isDownloadMenuOpen}
                  onClose={handleDownloadMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={handleDownloadPDF}>Download PDF</MenuItem>
                  <MenuItem onClick={handleDownloadCSVMenu}>Download Spreadsheet</MenuItem>
                </Menu>
              </>
              {onPmsSettingsUpdate && (
                <PmsActions
                  bill={bill}
                  localInvoices={localInvoices}
                  tz={tz}
                  structureName={structureName}
                  eventDetails={eventDetails}
                  trackEvent={trackEvent}
                  pmsType={pmsType}
                  pmsConfig={pmsConfig}
                  unitPmsNames={unitPmsNames}
                  unitPmsIds={unitPmsIds}
                  onPmsSettingsUpdate={onPmsSettingsUpdate}
                  structureUnits={structureUnits}
                  onStructureRefresh={onStructureRefresh}
                  customInvoiceAmountCents={customInvoiceAmountCents}
                  customInvoiceBreakdown={customInvoiceBreakdown}
                  customInvoiceLineItems={customInvoiceLineItems}
                />
              )}
              {/* ONLY show "Send Invoices" button if they havent been sent, and have billing enabled.
                  The loading-state spinner is gated on stripeConnect so that non-Stripe properties
                  (the common case now that Stripe is being sunsetted) don't flash a grey "Send Invoices"
                  button that resolves to null. Post-load branches still render Connect-Stripe for
                  properties that have BILLING enabled but haven't completed Stripe onboarding. */}
              {!effectiveBillsSentTimestamp &&
                needSendBillButton &&
                (billingStatusLoading ? (
                  stripeConnect ? (
                    <SWLoadingButton loading size="medium">
                      Send Invoices
                    </SWLoadingButton>
                  ) : null
                ) : billingEnabled && stripeSetupComplete ? (
                  // fully onboarded
                  <SWLoadingButton onClick={handleOpenSendInvoicesDialog}>
                    Send Invoices
                  </SWLoadingButton>
                ) : billingEnabled && !stripeSetupComplete ? (
                  // not yet onboarded
                  <SWLoadingButton onClick={() => setOpenConnectStripe(true)}>
                    Send Invoices
                  </SWLoadingButton>
                ) : null)}
              {needReportButton && !isConfirmReport && (
                <SWLoadingButton onClick={() => setIsConfirmReport(true)}>
                  Send Usage Report
                </SWLoadingButton>
              )}
              {/* Show "Generate Invoices" button for non-Stripe accounts with billable units, only if invoices haven't been generated yet */}
              {!stripeConnect &&
                (updatedBill ?? bill)?.units?.length > 0 &&
                localInvoices.length === 0 && (
                  <SWLoadingButton
                    onClick={handleOpenBatchGenerateDialog}
                    loading={isBatchGenerating}
                  >
                    Generate Invoices
                  </SWLoadingButton>
                )}
            </Box>
          )}
        </DialogActions>
        {isConfirmReport && (
          <DialogActions
            classes={{ root: classes.dialogActionsRoot }}
            sx={{
              justifyContent: 'space-between',
              px: 2,
              pb: 2,
              pt: 0,
            }}
          >
            <SWLoadingButtonOutlined onClick={() => setIsConfirmReport(false)}>
              Back
            </SWLoadingButtonOutlined>
            <SWLoadingButton onClick={handleSendReport}>
              {loading ? (
                <>
                  Loading...
                  <CircularProgress size={20} color="inherit" sx={{ marginLeft: '10px' }} />
                </>
              ) : (
                'Confirm Send'
              )}
            </SWLoadingButton>
          </DialogActions>
        )}

        {/* BILLING WITH STRIPE */}
        <Dialog
          open={openConfirmSendInvoices}
          onClose={() => {
            setOpenConfirmSendInvoices(false);
          }}
          aria-labelledby="confirm-send-invoices"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            id="confirm-send-invoices"
            sx={{ fontWeight: 700, fontSize: '24px', textAlign: 'center' }}
          >
            Ready to Send?
            <IconButton
              aria-label="close"
              onClick={() => {
                setOpenConfirmSendInvoices(false);
              }}
              size="small"
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack sx={{ px: 3 }}>
              <DialogContentText
                variant="body1"
                color="textSecondary"
                sx={{ mb: 2, px: 3, textAlign: 'left' }}
              >
                Please confirm you want to send invoices to your tenants. SSW invoice PDFs will also
                be generated with the settings below.
              </DialogContentText>

              {/* Days Until Due */}
              <Box sx={{ px: 3, mb: 2 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Days Until Due"
                  value={batchDueDateDays}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value) && value >= 1 && value <= 365) {
                      setBatchDueDateDays(value);
                    }
                  }}
                  inputProps={{ min: 1, max: 365 }}
                  size="small"
                  helperText={`Invoice due date: ${dayjs()
                    .tz(tz)
                    .add(batchDueDateDays, 'day')
                    .format('MMMM D, YYYY')}`}
                />
              </Box>

              {/* Pay Online URL */}
              <Box sx={{ px: 3, mb: 2 }}>
                <PayOnlineUrlField
                  value={batchPayOnlineUrl}
                  onChange={setBatchPayOnlineUrl}
                  structureId={bill.structure_id}
                  size="small"
                />
              </Box>

              {/* Comments */}
              <Box sx={{ px: 3, mb: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Comments"
                  value={batchPaymentInstructions}
                  onChange={(e) => setBatchPaymentInstructions(e.target.value)}
                  placeholder="Enter comments for tenants..."
                  helperText="Comments displayed on SSW invoice PDFs (optional)"
                  size="small"
                />
              </Box>

              {/* Save as Default Checkbox */}
              {onPaymentSettingsUpdate && (
                <Box sx={{ px: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={batchSaveAsDefault}
                        onChange={(e) => setBatchSaveAsDefault(e.target.checked)}
                      />
                    }
                    label="Save as default for this property"
                  />
                </Box>
              )}

              <UsageGraphModeTable
                units={(updatedBill ?? bill)?.units ?? []}
                overrides={batchUnitGraphModeOverrides}
                onOverridesChange={setBatchUnitGraphModeOverrides}
                saveAsDefault={batchSaveGraphModeOverrides}
                onSaveAsDefaultChange={setBatchSaveGraphModeOverrides}
                sx={{ px: 3, mt: 2, mb: 1 }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
            <SWLoadingButtonOutlined
              sx={{ minWidth: 120 }}
              onClick={() => {
                setOpenConfirmSendInvoices(false);
              }}
            >
              Back
            </SWLoadingButtonOutlined>
            <SWLoadingButton
              sx={{ minWidth: 120 }}
              onClick={async () => {
                // 1) close the "Ready to Send?" prompt
                setOpenConfirmSendInvoices(false);
                // 2) now fire wrapper
                handleSendInvoices();
              }}
              autoFocus
            >
              Confirm
            </SWLoadingButton>
          </DialogActions>
        </Dialog>

        {/* BILLING W/O STRIPE */}
        <Dialog
          open={openConnectStripe}
          onClose={() => {
            setOpenConnectStripe(false);
            setLoadingConnect(false);
          }}
          aria-labelledby="confirm-send-invoices"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            id="confirm-send-invoices"
            sx={{ fontWeight: 700, fontSize: '24px', textAlign: 'center' }}
          >
            Want to Send Invoices?
            <IconButton
              aria-label="close"
              onClick={() => {
                setOpenConnectStripe(false);
              }}
              size="small"
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack sx={{ px: 3 }}>
              <DialogContentText
                variant="body1"
                color="textSecondary"
                sx={{ mb: 0, px: 3, textAlign: 'left' }}
              >
                Please finish setting up your Stripe account to gain access to invoices.
              </DialogContentText>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
            <SWLoadingButtonOutlined
              sx={{ minWidth: 120 }}
              onClick={() => {
                setOpenConnectStripe(false);
                setLoadingConnect(false);
              }}
            >
              Back
            </SWLoadingButtonOutlined>
            <SWLoadingButton
              sx={{ minWidth: 120 }}
              onClick={async () => {
                setLoadingConnect(true);

                try {
                  // 1) Create / fetch onboarding URL
                  if (!stripeAccountExists) {
                    await createStripeAccount();
                  }
                  const { url } = await createStripeAccountLink({
                    link_type: STRIPE_ACCOUNT_LINK_TYPE.ACCOUNT_ONBOARDING,
                  });

                  if (!url) throw new Error('No Stripe URL returned');

                  // 2) Open Stripe in a fresh tab
                  window.open(url, '_blank');

                  // ✅ success toast
                  toast.success('Stripe setup opened in a new tab!', {
                    position: 'bottom-right',
                    closeButton: true,
                  });

                  // 3) Then close dialogs
                  onClose();
                } catch (err) {
                  console.error('Stripe setup failed', err);
                  // ❌ error toast
                  toast.error('Failed to set up Stripe. Please try again.', {
                    position: 'bottom-right',
                    closeButton: true,
                  });
                } finally {
                  setLoadingConnect(false);
                }
              }}
            >
              {loadingConnect
                ? 'Redirecting…'
                : stripeAccountExists
                ? 'Finish Stripe Setup'
                : 'Connect Stripe'}
            </SWLoadingButton>
          </DialogActions>
        </Dialog>

        {/* BATCH GENERATE CUSTOM INVOICES DIALOG */}
        <Dialog
          open={openBatchGenerateDialog}
          onClose={handleCloseBatchGenerateDialog}
          aria-labelledby="batch-generate-invoices-dialog"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            id="batch-generate-invoices-dialog"
            sx={{ fontWeight: 700, fontSize: '20px' }}
          >
            {isSendingStripeInvoices
              ? 'Sending Invoices...'
              : batchGenerationStarted
              ? stripeConnect
                ? 'Invoices Sent'
                : 'Invoices Queued'
              : 'Generate Invoices'}
            <IconButton
              aria-label="close"
              onClick={handleCloseBatchGenerateDialog}
              size="small"
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {isSendingStripeInvoices ? (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                  <CircularProgress size={48} sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Sending Stripe invoices to tenants...
                  </Typography>
                </Box>
              </>
            ) : !batchGenerationStarted ? (
              <>
                <DialogContentText sx={{ mb: 3 }}>
                  Configure payment settings for all invoices in this billing period. These will
                  appear on each generated PDF.
                </DialogContentText>

                {partialOccupancyUnitNames.length > 0 && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Some tenants occupied their unit for part of this billing period (
                    {partialOccupancyUnitNames.length === 1 ? 'unit' : 'units'}{' '}
                    <strong>{partialOccupancyUnitNames.join(', ')}</strong>). Each invoice covers
                    only that tenant's occupancy, so the generated invoices will add up to less than
                    the bill's Total Cost.
                  </Alert>
                )}

                {/* Pay Online URL */}
                <PayOnlineUrlField
                  value={batchPayOnlineUrl}
                  onChange={(next) => {
                    setBatchPayOnlineUrl(next);
                    setBatchUrlTouched(true);
                  }}
                  structureId={bill.structure_id}
                  sx={{ mb: 3 }}
                  disabled={isBatchGenerating}
                />

                {/* Comments */}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Comments"
                  value={batchPaymentInstructions}
                  onChange={(e) => {
                    setBatchPaymentInstructions(e.target.value);
                    setBatchInstructionsTouched(true);
                  }}
                  placeholder="Enter comments for tenants..."
                  helperText="Comments displayed on each invoice (optional)"
                  sx={{ mb: 3 }}
                  disabled={isBatchGenerating}
                />

                {/* Days Until Due */}
                <TextField
                  fullWidth
                  type="number"
                  label="Days Until Due"
                  value={batchDueDateDays}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value) && value >= 1 && value <= 365) {
                      setBatchDueDateDays(value);
                    }
                  }}
                  inputProps={{ min: 1, max: 365 }}
                  helperText={`Invoice due date: ${dayjs()
                    .tz(tz)
                    .add(batchDueDateDays, 'day')
                    .format('MMMM D, YYYY')}`}
                  sx={{ mb: 2 }}
                  disabled={isBatchGenerating}
                />

                {/* Save as Default Checkbox */}
                {onPaymentSettingsUpdate && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={batchSaveAsDefault}
                        onChange={(e) => setBatchSaveAsDefault(e.target.checked)}
                        disabled={isBatchGenerating}
                      />
                    }
                    label="Save as default for this property"
                  />
                )}

                <UsageGraphModeTable
                  units={(updatedBill ?? bill)?.units ?? []}
                  overrides={batchUnitGraphModeOverrides}
                  onOverridesChange={setBatchUnitGraphModeOverrides}
                  saveAsDefault={batchSaveGraphModeOverrides}
                  onSaveAsDefaultChange={setBatchSaveGraphModeOverrides}
                  disabled={isBatchGenerating}
                  sx={{ mt: 3, mb: 1 }}
                />

                {/* Invoice Count Info */}
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: '#E6F7FF',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <InfoOutlinedIcon sx={{ mr: 1, color: '#096DD9' }} />
                  <Typography variant="body2" color="#096DD9">
                    This will generate{' '}
                    {(updatedBill ?? bill)?.billable_units_count ??
                      (updatedBill ?? bill)?.units?.filter((u) => u.is_billable !== false).length ??
                      0}{' '}
                    SSW invoice PDFs. The PDFs will be available for download once processing is
                    complete.
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <DialogContentText sx={{ mb: 2 }}>
                  {stripeConnect
                    ? 'Stripe invoices have been sent to your tenants! SSW invoice PDFs are also being generated for your records.'
                    : 'Your SSW invoice PDFs are being generated. This process runs in the background and may take a few moments depending on the number of invoices.'}
                </DialogContentText>

                {/* Progress indicator */}
                {batchGenerationProgress && (
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor:
                        batchGenerationProgress.ready >= batchGenerationProgress.total
                          ? '#DFF7DF'
                          : '#E6F7FF',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    {isPolling && batchGenerationProgress.ready < batchGenerationProgress.total && (
                      <CircularProgress size={16} sx={{ mr: 1.5 }} />
                    )}
                    <Typography
                      variant="body2"
                      color={
                        batchGenerationProgress.ready >= batchGenerationProgress.total
                          ? '#198754'
                          : '#096DD9'
                      }
                      sx={{ fontWeight: 500 }}
                    >
                      {batchGenerationProgress.ready >= batchGenerationProgress.total
                        ? `✓ All ${batchGenerationProgress.total} invoice PDFs are ready!`
                        : `${batchGenerationProgress.ready} of ${batchGenerationProgress.total} invoice PDFs ready...`}
                    </Typography>
                  </Box>
                )}

                {!batchGenerationProgress && (
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: '#DFF7DF',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" color="#198754">
                      ✓ {batchCustomInvoices.length} invoice PDFs queued for generation
                    </Typography>
                  </Box>
                )}

                <Typography variant="body2" color="text.secondary">
                  {batchGenerationProgress &&
                  batchGenerationProgress.ready >= batchGenerationProgress.total
                    ? 'All invoices are ready! Close this dialog and select the invoices you want to download.'
                    : 'You can close this dialog and check back later. Select invoices to download when ready.'}
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            {isSendingStripeInvoices ? null : !batchGenerationStarted ? ( // No buttons while sending Stripe invoices - user must wait
              <>
                <SWLoadingButtonOutlined
                  onClick={handleCloseBatchGenerateDialog}
                  disabled={isBatchGenerating}
                >
                  Cancel
                </SWLoadingButtonOutlined>
                <SWLoadingButton onClick={handleBatchGenerate} loading={isBatchGenerating}>
                  Generate All Invoices
                </SWLoadingButton>
              </>
            ) : (
              <SWLoadingButton onClick={handleCloseBatchGenerateDialog}>Close</SWLoadingButton>
            )}
          </DialogActions>
        </Dialog>

        {/* SEND INVOICES BY EMAIL DIALOG */}
        <Dialog
          open={openSendEmailDialog}
          onClose={() => !isSendingEmails && setOpenSendEmailDialog(false)}
          aria-labelledby="send-email-dialog"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="send-email-dialog">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Send Invoices by Email</Typography>
              <IconButton
                onClick={() => setOpenSendEmailDialog(false)}
                disabled={isSendingEmails}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              The following <strong>{selectedEmailRows.length}</strong> invoice(s) will be emailed
              to their billing contacts:
            </DialogContentText>
            {alreadySentCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {alreadySentCount === selectedEmailRows.length
                  ? 'All selected invoices have already been emailed.'
                  : `${alreadySentCount} of ${selectedEmailRows.length} selected invoice(s) have already been emailed.`}{' '}
                Sending again will deliver a duplicate email.
              </Alert>
            )}
            {skippedCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {skippedCount} invoice{skippedCount !== 1 ? 's have' : ' has'} no valid recipient
                for this billing period and will be skipped.
              </Alert>
            )}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Unit</TableCell>
                    <TableCell>Full Name</TableCell>
                    <TableCell>Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedEmailRows.map((row, idx) => (
                    <TableRow key={idx} sx={row.noValidRecipient ? { opacity: 0.5 } : undefined}>
                      <TableCell>{row.unitName}</TableCell>
                      <TableCell sx={row.noValidRecipient ? { color: 'error.main' } : undefined}>
                        {row.fullName}
                      </TableCell>
                      <TableCell sx={row.noValidRecipient ? { fontStyle: 'italic' } : undefined}>
                        {row.email}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <SWLoadingButtonOutlined
              onClick={() => setOpenSendEmailDialog(false)}
              disabled={isSendingEmails}
            >
              Cancel
            </SWLoadingButtonOutlined>
            <SWLoadingButton
              onClick={handleSendInvoiceEmails}
              loading={isSendingEmails}
              disabled={allWillBeSkipped}
            >
              Send Emails
            </SWLoadingButton>
          </DialogActions>
        </Dialog>
      </Dialog>
    </>
  );
};

interface IRenderConfirmReport {
  propertyName: string;
  address: string;
  dateRange: string;
}

const RenderConfirmReport = (props: IRenderConfirmReport) => {
  const { hasHourlyUsage } = useUserPermissions();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={'bold'} sx={{ mb: 1 }}>
          Send Usage Reports
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Water usage reports will be sent via email to the tenants for each unit.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please check the information below and confirm send.
        </Typography>
      </Box>

      {/* mx needed for Divider to reach full length */}
      <Divider sx={{ mx: -3, mb: 5 }} />

      <Box sx={{ px: 1 }}>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography fontWeight={'bold'}>Property:</Typography>
            <Typography>{props.propertyName}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography fontWeight={'bold'}>Address:</Typography>
            <Typography>{props.address}</Typography>
          </Box>
          {/* pb is padding between Date Range and Divider */}
          <Box sx={{ display: 'flex', gap: 1, pb: hasHourlyUsage ? 2 : 5 }}>
            <Typography fontWeight={'bold'}>Date range:</Typography>
            <Typography>{props.dateRange}</Typography>
          </Box>
          {hasHourlyUsage && (
            <Alert severity="info" sx={{ mb: 5 }}>
              Tenant usage reports always show <strong>daily</strong> totals. The hourly view you
              see in the app is not included in the email sent to tenants.
            </Alert>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

interface InvoiceWithSubs {
  id: string; // BillingInvoice UUID for custom PDF generation
  unitId: string;
  subscriberId: string;
  stripeInvoiceId: string;
  stripeLastStatus: string;
  stripeInvoicePdfUrl: string | null;
  stripeTotal: number;
  stripeCurrency: string;
  customInvoicePdfUrl: string | null; // Custom invoice PDF URL from S3
}
interface RenderTableProps {
  bill: IUpdatedBillingData;
  invoices: InvoiceWithSubs[];
  subscriberById: Map<string, ISubscriber>;
  billingContactWindowsByUnit: Map<string, BillingContactWindow[]>;
  autoDownload: 'csv' | 'pdf' | null;
  title: string;
  onClose: Function;
  eventDetails: IEventDetails;
  timeZone: string;
  _needStatus?: boolean;
  billsSentTimestamp: number | null;
  stripeConnect: boolean;
  billingEnabled: boolean;
  needSendBillButton?: boolean;
  forceShowAll?: boolean;
  onInvoicePdfGenerated?: (invoiceId: string, pdfUrl: string) => void;
  payOnlineUrl?: string | null;
  paymentInstructions?: string | null;
  dueDateDays?: number | null;
  onPaymentSettingsUpdate?: (
    payOnlineUrl: string | null,
    paymentInstructions: string | null,
    dueDateDays: number | null,
  ) => Promise<void>;
  // Checkbox selection for batch download
  selectedInvoiceIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  showSelectionCheckboxes?: boolean;
  // Invoices with pending custom invoice generation
  pendingCustomInvoiceIds?: Set<string>;
  // Actual due dates for custom invoices (keyed by billing_invoice_id)
  customInvoiceDueDates?: Map<string, string>;
  // Map of invoice ID → email_sent_at unix timestamp
  emailSentMap?: Map<string, number>;
  // Map of invoice ID → snapshot of pay_online_url / payment_instructions
  // captured when that custom invoice was generated. Drives the "i" tooltip
  // on past invoices so users can see what URL/comments were used.
  customInvoicePaymentInfoMap?: Map<
    string,
    { payOnlineUrl: string | null; paymentInstructions: string | null }
  >;
  // Map of billing_invoice_id → per-tenant prorated amount in cents. Populated by
  // the download-urls polling response once the PDF has been generated. The "Invoice
  // Amount" column prefers this over the un-prorated unit total when available; for
  // legacy invoices without this field the column falls back to the per-unit total.
  customInvoiceAmountCents?: Map<string, number>;
  // Per-category breakdown matching the PDF (usage / billable common area / fees).
  // When present, the row tooltip renders these directly (collapsed view). When
  // absent (legacy invoices), the tooltip falls back to the per-fee scaling
  // approximation in buildFeeBreakdown.
  customInvoiceBreakdown?: Map<
    string,
    { usageCents: number; commonAreaCents: number; feeCents: number }
  >;
  // Frozen rendered PDF lines per invoice; display source of truth when present
  customInvoiceLineItems?: Map<string, CustomInvoiceLineItem[]>;
  // Selection action callbacks (rendered in table toolbar)
  onDownloadSelected?: () => void;
  onSendEmail?: () => void;
  isSendingEmails?: boolean;
}

// StatusChip component for displaying invoice status with color (replaced renderStatusColor)
const StatusChip = ({ status }: { status: string }) => {
  let color = '#E0E0E0';
  let textColor = '#5A5A5A';
  // Display "not_created" as "None" since it means no Stripe invoice exists yet
  let displayStatus = status === 'not_created' ? 'None' : status;

  switch (status) {
    case 'not_created':
    case 'None':
      color = '#E0E0E0';
      break;
    case 'draft':
      color = '#D0E9FF';
      break;
    case 'open':
      color = '#FFEFD0';
      break;
    case 'paid':
      color = '#DFF7DF';
      textColor = '#198754';
      break;
    case 'uncollectible':
    case 'uncollectable':
      color = '#FFD6D6';
      textColor = '#D32F2F';
      break;
    case 'void':
      color = '#E4D6FF';
      break;
    default:
      color = '#E0E0E0';
  }
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: color,
        color: textColor,
        borderRadius: '16px',
        fontWeight: 'bold',
        padding: '4px 12px',
        fontSize: '0.875rem',
        textTransform: 'uppercase',
        boxShadow: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {displayStatus.replace('_', ' ').toUpperCase()}
    </Box>
  );
};

const RenderTable = ({
  bill,
  invoices,
  subscriberById,
  billingContactWindowsByUnit,
  autoDownload,
  title,
  onClose,
  eventDetails,
  timeZone,
  _needStatus = true,
  billsSentTimestamp,
  stripeConnect,
  billingEnabled,
  needSendBillButton,
  forceShowAll = false,
  onInvoicePdfGenerated,
  payOnlineUrl,
  paymentInstructions,
  dueDateDays,
  onPaymentSettingsUpdate,
  selectedInvoiceIds,
  onSelectionChange,
  showSelectionCheckboxes = false,
  pendingCustomInvoiceIds = new Set(),
  customInvoiceDueDates = new Map(),
  emailSentMap = new Map(),
  customInvoicePaymentInfoMap = new Map(),
  customInvoiceAmountCents = new Map(),
  customInvoiceBreakdown = new Map(),
  customInvoiceLineItems = new Map(),
  onDownloadSelected,
  onSendEmail,
  isSendingEmails = false,
}: RenderTableProps) => {
  const [showAllForPDF, setShowAllForPDF] = React.useState(false);
  const { toPDF, targetRef } = useGeneratePDF({
    filename: `${title.replace(/ /g, '_')}_report.pdf`,
  });
  const [alredyExported, setAlredyExported] = useState<boolean>(false);
  const [generatingPdfFor, setGeneratingPdfFor] = useState<string | null>(null);

  const tz = timeZone === '' ? moment.tz.guess() : timeZone;

  // State for Generate Invoice PDF dialog
  const [generatePdfDialogOpen, setGeneratePdfDialogOpen] = useState(false);
  const [pendingInvoice, setPendingInvoice] = useState<{
    billingId: string;
    invoiceId: string;
  } | null>(null);
  const [dialogPayOnlineUrl, setDialogPayOnlineUrl] = useState(payOnlineUrl || '');
  const [dialogPaymentInstructions, setDialogPaymentInstructions] = useState(
    paymentInstructions || '',
  );
  const [dialogUrlTouched, setDialogUrlTouched] = useState(false);
  const [dialogInstructionsTouched, setDialogInstructionsTouched] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Reset dialog fields when payOnlineUrl/paymentInstructions props change
  React.useEffect(() => {
    setDialogPayOnlineUrl(payOnlineUrl || '');
    setDialogPaymentInstructions(paymentInstructions || '');
  }, [payOnlineUrl, paymentInstructions]);

  // Custom PDF handler that shows all rows
  const handlePDFExport = React.useCallback(async () => {
    setShowAllForPDF(true);
    // Wait for re-render with all rows visible
    await new Promise((resolve) => setTimeout(resolve, 100));
    await toPDF();
    setShowAllForPDF(false);
  }, [toPDF]);

  // Opens dialog instead of directly generating PDF
  const openGeneratePdfDialog = React.useCallback(
    (billingId: string, invoiceId: string) => {
      setPendingInvoice({ billingId, invoiceId });
      // Pre-fill with current defaults (already set via useEffect, but reset for safety)
      setDialogPayOnlineUrl(payOnlineUrl || '');
      setDialogPaymentInstructions(paymentInstructions || '');
      setDialogUrlTouched(false);
      setDialogInstructionsTouched(false);
      setSaveAsDefault(false);
      setGeneratePdfDialogOpen(true);
    },
    [payOnlineUrl, paymentInstructions],
  );

  // Handler for generating custom invoice PDF via API (called after dialog confirmation)
  const handleGenerateCustomPDF = React.useCallback(
    async (
      billingId: string,
      invoiceId: string,
      options?: { pay_online_url?: string; payment_instructions?: string },
    ) => {
      setGeneratingPdfFor(invoiceId);
      try {
        const result = await generateInvoicePDF(billingId, invoiceId, options);
        // Update local state with the new PDF URL
        if (onInvoicePdfGenerated) {
          onInvoicePdfGenerated(invoiceId, result.download_url);
        }
        // Open the download URL in a new tab
        window.open(result.download_url, '_blank');
        toast.success('Invoice PDF generated successfully!', { closeButton: true });
      } catch (error) {
        // Error toast is handled in the API function
        console.error('Failed to generate custom PDF:', error);
      } finally {
        setGeneratingPdfFor(null);
      }
    },
    [onInvoicePdfGenerated],
  );

  // Handler for dialog confirmation
  const handleDialogConfirm = React.useCallback(async () => {
    if (!pendingInvoice) return;

    // If user touched a field, always send it (even if empty) to override defaults
    // If user didn't touch, don't send it so backend can use property defaults
    const options: { pay_online_url?: string; payment_instructions?: string } = {};
    const urlValue = dialogPayOnlineUrl.trim();
    const instructionsValue = dialogPaymentInstructions.trim();

    if (dialogUrlTouched || urlValue) {
      options.pay_online_url = urlValue;
    }
    if (dialogInstructionsTouched || instructionsValue) {
      options.payment_instructions = instructionsValue;
    }

    // If "Save as default" is checked, update the property settings first
    if (saveAsDefault && onPaymentSettingsUpdate) {
      setIsSavingSettings(true);
      try {
        await onPaymentSettingsUpdate(
          urlValue || null,
          instructionsValue || null,
          dueDateDays ?? null,
        );
        toast.success('Payment settings saved as default!', { closeButton: true });
      } catch (error) {
        console.error('Failed to save default settings:', error);
        // Continue with PDF generation even if saving defaults fails
      } finally {
        setIsSavingSettings(false);
      }
    }

    // Close dialog and generate PDF
    setGeneratePdfDialogOpen(false);
    await handleGenerateCustomPDF(pendingInvoice.billingId, pendingInvoice.invoiceId, options);
    setPendingInvoice(null);
  }, [
    pendingInvoice,
    dialogPayOnlineUrl,
    dialogPaymentInstructions,
    dialogInstructionsTouched,
    dialogUrlTouched,
    saveAsDefault,
    onPaymentSettingsUpdate,
    handleGenerateCustomPDF,
    dueDateDays,
  ]);

  // Handler for dialog cancel
  const handleDialogCancel = React.useCallback(() => {
    setGeneratePdfDialogOpen(false);
    setPendingInvoice(null);
  }, []);

  const { trackEvent } = useMixPanel();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Checkbox selection handler for individual rows
  const handleSelectRow = React.useCallback(
    (invoiceId: string, checked: boolean) => {
      if (!onSelectionChange || !selectedInvoiceIds) return;
      const newSelection = new Set(selectedInvoiceIds);
      if (checked) {
        newSelection.add(invoiceId);
      } else {
        newSelection.delete(invoiceId);
      }
      onSelectionChange(newSelection);
    },
    [onSelectionChange, selectedInvoiceIds],
  );

  const cellSX = {
    py: 2,
    px: 3,
    borderBottom: 1,
    borderColor: 'divider',
  };

  const UNIT_LABELS: Record<
    'GALLONS' | 'GALLONS_100' | 'GALLONS_1K' | 'LITERS' | 'LITERS_1K' | 'CF' | 'CCF',
    string
  > = {
    GALLONS: 'Gallons',
    GALLONS_100: '100 Gallons',
    GALLONS_1K: '1000 Gallons',
    LITERS: 'Liters',
    LITERS_1K: '1000 Liters',
    CF: 'CF (Cubic Feet)',
    CCF: 'CCF (Centum Cubic Feet)',
  };

  // Helper function to build fee breakdown for tooltip
  // includeCommonArea: true for Invoice Amount tooltip, false for Unit Cost tooltip
  // prorationRatio: scales every line item below (common area split, FLAT, SPLIT, UNIT_FLAT).
  //   For non-prorated rows pass 1 (default). For prorated multi-tenant rows pass the row's
  //   resolvedInvoiceAmount / un-prorated invoiceAmount so the per-line numbers in the tooltip
  //   match the per-tenant amount shown in the column (no more un-prorated common-area number).
  //   Caller is expected to have already prorated waterCost, so we don't double-scale that here.
  const buildFeeBreakdown = (
    unitId: string,
    _unitName: string,
    waterCost: number,
    _feeAmount: number,
    isBillable: boolean,
    includeCommonArea: boolean = true,
    prorationRatio: number = 1,
  ) => {
    const breakdown: { label: string; amount: number }[] = [];

    // Add water usage cost first (unit's own usage). waterCost is already prorated by caller.
    breakdown.push({ label: 'Water Usage', amount: waterCost });

    // Add common area usage for billable units when includeCommonArea is true
    if (isBillable && includeCommonArea) {
      const commonAreaUsage = bill.common_area_usage ?? [];
      commonAreaUsage.forEach((ca) => {
        breakdown.push({
          label: `Common area: ${ca.name}`,
          amount: ca.split_cost * prorationRatio,
        });
      });
    }

    // Process line items from bill (generateBill API - snake_case)
    // This ensures consistency with bill.units which comes from the same API
    const billLineItems = bill.line_items ?? [];
    const splitFees = billLineItems.filter((item) => item.type === 'SPLIT');
    const flatFees = billLineItems.filter((item) => item.type === 'FLAT');
    const unitFlatFees = billLineItems.filter(
      (item) => item.type === 'UNIT_FLAT' && item.unit_id === unitId,
    );
    // Use billable_units_count from API if available, otherwise fallback to total units
    const billableUnitsCount = bill.billable_units_count ?? bill.units.length;

    // Only show split and flat fees for billable units
    if (isBillable) {
      // Add SPLIT fees (divided by number of billable units, then by tenant proration)
      splitFees.forEach((fee) => {
        const splitAmount = ((fee.cost ?? 0) / billableUnitsCount) * prorationRatio;
        breakdown.push({ label: fee.description || 'Split Fee', amount: splitAmount });
      });

      // Add FLAT fees (applied to all units, scaled per-tenant)
      flatFees.forEach((fee) => {
        breakdown.push({
          label: fee.description || 'Flat Fee',
          amount: (fee.cost ?? 0) * prorationRatio,
        });
      });
    }

    // Add UNIT_FLAT fees (specific to this unit) - these can apply to any unit, scaled per-tenant
    unitFlatFees.forEach((fee) => {
      breakdown.push({
        label: fee.description || 'Unit-Specific Fee',
        amount: (fee.cost ?? 0) * prorationRatio,
      });
    });

    return breakdown;
  };

  // Tooltip line-item source. When the backend has persisted per-category cents
  // (custom_invoices.usage_cost_cents / billable_common_area_cost_cents /
  // fee_cost_cents), collapse named fees into a single "Fees" line so the
  // displayed numbers match the PDF exactly. Otherwise fall back to the
  // legacy named-fee approximation.
  const getBreakdownItems = (
    row: {
      unitId: string;
      unitName: string;
      waterCost: number;
      estimatedCost?: number;
      feeAmount: number;
      commonAreaCost: number;
      isBillable: boolean;
      prorationRatio: number;
      hasPersistedBreakdown: boolean;
      lineItems?: CustomInvoiceLineItem[] | null;
    },
    includeCommonArea: boolean,
  ): { label: string; amount: number }[] => {
    const estimatedCost = row.estimatedCost ?? 0;
    if (row.lineItems && row.lineItems.length > 0) {
      // Frozen rendered lines: show exactly what the PDF shows, one tooltip
      // line per PDF line, no reconstruction of any kind
      return row.lineItems
        .filter((li) => includeCommonArea || li.category !== 'common_area')
        .map((li) => ({ label: li.description, amount: li.amount_cents / 100 }));
    }
    if (row.hasPersistedBreakdown) {
      // Drop zero-value lines so the tooltip doesn't claim "Fees: $0.00" on
      // structures with no service fees, etc. Water Usage stays always since
      // it's the canonical first row.
      // The persisted usage cents INCLUDE estimated (the PDF groups them into the
      // usage category), so split it back out into its own line here.
      // No floor: a negative metered value (reversed meter on a legacy invoice)
      // stays visible so the tooltip lines sum to the row total
      const items: { label: string; amount: number }[] = [
        { label: 'Water Usage', amount: row.waterCost - estimatedCost },
      ];
      if (estimatedCost > 0) {
        items.push({ label: 'Estimated Usage', amount: estimatedCost });
      }
      if (includeCommonArea && row.isBillable && row.commonAreaCost > 0) {
        items.push({ label: 'Common Area', amount: row.commonAreaCost });
      }
      if (row.feeAmount > 0) {
        items.push({ label: 'Fees', amount: row.feeAmount });
      }
      return items;
    }
    const fallbackItems = buildFeeBreakdown(
      row.unitId,
      row.unitName,
      row.waterCost,
      row.feeAmount,
      row.isBillable,
      includeCommonArea,
      row.prorationRatio,
    );
    if (estimatedCost > 0) {
      // Fallback waterCost is metered-only (unit_cost_meter); estimated is additive
      fallbackItems.splice(1, 0, { label: 'Estimated Usage', amount: estimatedCost });
    }
    return fallbackItems;
  };

  useEffect(() => {
    if (autoDownload && !alredyExported) {
      if (autoDownload === 'pdf') {
        setAlredyExported(true);
        handlePDFExport().then(() => {
          trackEvent('Bill Downloaded', eventDetails);
          onClose();
        });
      }
    }
  }, [autoDownload, handlePDFExport, alredyExported, onClose, trackEvent, eventDetails]);

  // Show invoice columns when:
  // 1. Stripe invoices have been sent (stripeConnect && billsSentTimestamp)
  // 2. OR invoices exist (for custom invoice PDF generation)
  const hasInvoices = invoices.length > 0;
  const showInvoiceColumns = (stripeConnect && !!billsSentTimestamp) || hasInvoices;
  // Before any invoice exists, still show WHO will be invoiced (and their clipped
  // occupancy windows) so totals that will come out prorated are no surprise.
  const showProspectiveTenants = !showInvoiceColumns && billingContactWindowsByUnit.size > 0;
  const showTenantColumn = showInvoiceColumns || showProspectiveTenants;
  // Status column only shown for Stripe-connected accounts (non-Stripe always shows "NONE")
  const showStatusColumn = stripeConnect && !!billsSentTimestamp;
  // Due date column shown for Stripe accounts with bills sent, OR non-Stripe accounts with generated invoices
  const hasCustomInvoicesWithDueDates = customInvoiceDueDates.size > 0;
  const showDueDateColumn = showStatusColumn || hasCustomInvoicesWithDueDates;
  const showEmailSentColumn = !stripeConnect && emailSentMap.size > 0;

  // Collect the distinct (pay_online_url, payment_instructions) snapshots
  // across the invoices in this bill. The batch endpoint sends one URL for
  // every invoice it generates, so in the common case there is exactly one
  // distinct snapshot — we surface that as a single tooltip in the column
  // header rather than repeating an icon on every row. If a user used the
  // per-invoice generate dialog to set a different URL on one row, the
  // distinct count goes above 1 and the per-row icons take over.
  const distinctPaymentInfos: Array<{
    payOnlineUrl: string | null;
    paymentInstructions: string | null;
  }> = [];
  {
    // Restrict the distinct-count to invoices that actually exist in this bill right
    // now. customInvoicePaymentInfoMap merges entries across generation runs and isn't
    // pruned on regenerate, so it can hold stale entries for billing_invoice_ids that
    // were deleted when the user clicked "Regenerate Invoices". Counting those stale
    // entries would push distinctPaymentInfos above 1 and incorrectly switch the
    // column-header tooltip into per-row mode.
    const currentInvoiceIds = new Set(invoices.map((inv) => inv.id).filter(Boolean) as string[]);
    const seen = new Set<string>();
    customInvoicePaymentInfoMap.forEach((info, invoiceId) => {
      if (!currentInvoiceIds.has(invoiceId)) return;
      const key = `${info.payOnlineUrl ?? ''}||${info.paymentInstructions ?? ''}`;
      if (!seen.has(key)) {
        seen.add(key);
        distinctPaymentInfos.push(info);
      }
    });
  }
  const sharedPaymentInfo = distinctPaymentInfos.length === 1 ? distinctPaymentInfos[0] : null;
  const hasMixedPaymentInfo = distinctPaymentInfos.length > 1;

  // Calculate total common area split cost (only applies to billable units)
  const commonAreaUsage = bill.common_area_usage ?? [];
  const totalCommonAreaSplitCost = commonAreaUsage.reduce((sum, ca) => sum + ca.split_cost, 0);

  // fall back to bill.units if there are no invoices yet
  // Show invoice data when invoices exist (for both Stripe and custom invoices)
  const rows = bill.units
    .flatMap((unit) => {
      // invoices come from bills API which converts to camelCase
      const unitInvoices = invoices.filter((inv) => inv.unitId === unit.id);
      const isBillable = unit.is_billable ?? true; // Default to true for backwards compatibility

      // Estimated usage is charged on the invoice (the PDF renders it as its own
      // line item), so the preview amounts include it; tooltips itemize it separately
      const unitEstimatedCost = unit.unit_estimated_cost ?? 0;

      // unit_cost already includes: common area split + split fees + flat fees + unit flat fees
      // Invoice amount = water usage + estimated usage + unit_cost (what appears on the invoice)
      const invoiceAmount = (unit.unit_cost_meter ?? 0) + unitEstimatedCost + (unit.unit_cost ?? 0);

      // Unit cost = invoice amount minus common area split (for billable units)
      // This represents just the unit's water usage + fees, without common area charges
      const unitCost = isBillable ? invoiceAmount - totalCommonAreaSplitCost : invoiceAmount;

      // feeAmount includes common area, so we need feeAmountWithoutCommonArea for Unit Cost tooltip
      const feeAmountWithCommonArea = unit.unit_cost ?? 0;
      const feeAmountWithoutCommonArea = isBillable
        ? feeAmountWithCommonArea - totalCommonAreaSplitCost
        : feeAmountWithCommonArea;

      if (unitInvoices.length > 0) {
        // Order multi-tenant prorated rows by each tenant's move-in date (earliest first)
        // so the column visually mirrors the occupancy timeline. Without an explicit sort
        // the order is whatever the API returned, which is undefined.
        const moveInForInvoice = (inv: any): number => {
          const sub = inv.subscriberId ? subscriberById.get(inv.subscriberId) : undefined;
          const us = sub?.unit_subscriptions?.find((u) => u.unit_id === unit.id);
          return us?.move_in_date ? new Date(us.move_in_date).getTime() : 0;
        };
        const orderedInvoices = [...unitInvoices].sort(
          (a, b) => moveInForInvoice(a) - moveInForInvoice(b),
        );
        return orderedInvoices.map((inv) => {
          const subscriber = inv.subscriberId ? subscriberById.get(inv.subscriberId) : undefined;
          // Use actual custom invoice due date if available, otherwise calculate from billsSentTimestamp
          const customDueDate = customInvoiceDueDates.get(inv.id);
          const dueDate = getDueDate(
            customDueDate,
            billsSentTimestamp ?? bill.end_time,
            dueDateDays ?? 21,
            timeZone,
          );
          // Prefer per-invoice amounts when we have them:
          //   1. Stripe path: stripe_total stored on the BillingInvoice at Stripe-send time
          //   2. NonBilling path: amount_due_cents stored on the CustomInvoice at PDF-render time
          //      (the actual prorated per-tenant total)
          // Fall back to the un-prorated unit total when neither is available, which only
          // happens for invoices generated before amount_due_cents was persisted.
          const persistedCents = customInvoiceAmountCents.get(inv.id);
          const persistedDollars = persistedCents !== undefined ? persistedCents / 100 : undefined;
          const resolvedInvoiceAmount = inv.stripeTotal ?? persistedDollars ?? invoiceAmount;
          // Proportionally scale the other amount columns (Unit Cost, Water Cost, Fees) when
          // the resolved invoice amount differs from the un-prorated unit total. This keeps
          // the row internally consistent: Unit Cost + common-area-share = Invoice Amount.
          // The backend allocates FEES via cumulative-truncation on days and USAGE per-tenant
          // metered, so this ratio is an approximation, but ratio * unit_total matches within
          // cents in practice and is what the per-row tooltip needs to look right.
          const prorationRatio =
            invoiceAmount > 0 && resolvedInvoiceAmount !== invoiceAmount
              ? resolvedInvoiceAmount / invoiceAmount
              : 1;
          // Prefer the per-category breakdown the backend persisted on the custom_invoice
          // (matches the PDF exactly). Falls back to scaling the unit-level numbers by
          // prorationRatio (approximation) when the backend hasn't persisted the breakdown
          // yet, e.g. invoices generated before the migration ran.
          const persistedBreakdown = customInvoiceBreakdown.get(inv.id);
          // Frozen rendered lines for this invoice; when present they are the
          // display source of truth for the tooltip and the estimated split
          const persistedLineItems = customInvoiceLineItems.get(inv.id) ?? null;
          const rowWaterCost = persistedBreakdown
            ? persistedBreakdown.usageCents / 100
            : (unit.unit_cost_meter ?? 0) * prorationRatio;
          const rowCommonAreaCost = persistedBreakdown
            ? persistedBreakdown.commonAreaCents / 100
            : totalCommonAreaSplitCost * prorationRatio;
          const rowFeeAmount = persistedBreakdown
            ? persistedBreakdown.feeCents / 100
            : feeAmountWithoutCommonArea * prorationRatio;
          // Unit Cost = water + fees (excludes common area). For the persisted path this
          // mirrors the PDF's own decomposition; for the fallback it preserves the
          // existing approximation.
          const rowUnitCost = persistedBreakdown
            ? rowWaterCost + rowFeeAmount
            : unitCost * prorationRatio;
          return {
            id: inv.stripeInvoiceId || inv.id,
            invoiceId: inv.id as string | null, // BillingInvoice UUID for custom PDF generation
            billingId: bill.billing_id,
            unitId: unit.id,
            unitName: unit.name,
            tenant: subscriber ? `${subscriber.first_name} ${subscriber.last_name}` : '—',
            usage: unit.unit_total_volume,
            estimatedUsage: unit.unit_estimated_volume ?? 0,
            // Per-tenant estimated cost from the frozen rendered lines when
            // available. The ratio fallback distributes the unit's estimate by
            // dollar share, which has no relationship to occupancy windows (it
            // can attribute estimated cost to a tenant whose occupancy overlaps
            // no estimate at all), so it survives only for legacy invoices.
            estimatedCost: persistedLineItems
              ? persistedLineItems
                  .filter((li) => li.category === 'estimated_usage')
                  .reduce((sum, li) => sum + li.amount_cents, 0) / 100
              : (unit.unit_estimated_cost ?? 0) * prorationRatio,
            volumeUnit: eventDetails.VolumeUnit,
            unitCost: rowUnitCost, // Per-tenant share of unit cost (excludes common area)
            invoiceAmount: resolvedInvoiceAmount, // Per-tenant prorated total when known; otherwise unit total
            waterCost: rowWaterCost,
            feeAmount: rowFeeAmount, // Per-tenant share of fees without common area (for Unit Cost tooltip)
            commonAreaCost: rowCommonAreaCost, // Used by the per-row tooltip when persistedBreakdown is present
            prorationRatio, // For row tooltips so per-line amounts (common area split, FLAT, etc.) scale too
            hasPersistedBreakdown: persistedBreakdown !== undefined, // True when tooltip should show collapsed PDF-matching view
            lineItems: persistedLineItems, // Frozen PDF lines; tooltip renders these verbatim when present
            dueDate: dueDate as number | null,
            status: inv.stripeLastStatus,
            pdfUrl: inv.stripeInvoicePdfUrl,
            customPdfUrl: inv.customInvoicePdfUrl,
            isBillable,
            emailSentAt: emailSentMap.get(inv.id) ?? null,
            paymentInfoSnapshot: customInvoicePaymentInfoMap.get(inv.id) ?? null,
            // TODO: Uncomment when stripe_invoice_url feature is ready for deployment
            // stripeInvoiceUrl: inv.stripeInvoiceUrl,
          };
        });
      } else {
        // Unit Cost / Water Cost / Fee columns show what this unit ACTUALLY COST the
        // owner regardless of billability, so keep their real values for common-area
        // units. Invoice Amount is what the unit owes, which for non-billable / common-
        // area units is $0 because their cost gets distributed to billable units via the
        // SPLIT line item (i.e., nobody pays the invoice on a common area row directly).
        const displayInvoiceAmount = isBillable ? invoiceAmount : 0;
        return [
          {
            id: unit.id,
            invoiceId: null as string | null, // No invoice yet
            billingId: bill.billing_id,
            unitId: unit.id,
            unitName: unit.name,
            tenant: '-',
            usage: unit.unit_total_volume,
            estimatedUsage: unit.unit_estimated_volume ?? 0,
            estimatedCost: unit.unit_estimated_cost ?? 0,
            volumeUnit: eventDetails.VolumeUnit,
            unitCost,
            invoiceAmount: displayInvoiceAmount,
            waterCost: unit.unit_cost_meter ?? 0,
            feeAmount: feeAmountWithoutCommonArea,
            commonAreaCost: isBillable ? totalCommonAreaSplitCost : 0,
            prorationRatio: 1, // Non-prorated placeholder row; no scaling needed in tooltip
            hasPersistedBreakdown: false, // Placeholder rows have no invoice → use named-fee tooltip
            dueDate: null as number | null, // No invoice yet, so no due date
            status: 'None',
            pdfUrl: null as string | null,
            customPdfUrl: null as string | null,
            isBillable,
            emailSentAt: null as number | null,
            paymentInfoSnapshot: null as {
              payOnlineUrl: string | null;
              paymentInstructions: string | null;
            } | null,
            // TODO: Uncomment when stripe_invoice_url feature is ready for deployment
            // stripeInvoiceUrl: null,
          },
        ];
      }
    })
    // Sort by unitName for display order, then break ties by unitId so rows for the
    // SAME unit are guaranteed contiguous even when multiple distinct units share a
    // display name. (Without the unitId tie-break, two units literally named
    // "same name" can interleave their tenant rows, which then breaks the rowSpan
    // grouping below — invoice cells render under the wrong unit and paginating
    // mid-group leaves the group's first cells stranded on the previous page.)
    // Within one unit, preserve the move-in-ordered insertion order from the per-unit
    // map above (stable sort). Don't tiebreak by tenant name here, that would shuffle
    // multi-tenant prorated rows back into alphabetic order.
    .sort((a, b) => {
      const byName = naturalCompare(a.unitName, b.unitName);
      if (byName !== 0) return byName;
      return naturalCompare(a.unitId, b.unitId);
    });

  const start = page * rowsPerPage;
  // Show all rows if forceShowAll or showAllForPDF is true, otherwise use pagination
  const shouldShowAll = forceShowAll || showAllForPDF;
  const displayRows = shouldShowAll ? rows : rows.slice(start, start + rowsPerPage);

  // Count rows per UNIT *within the visible page* (keyed on unitId, not unitName).
  // Keying on unitId stops two units that share a display name from being merged
  // into one rowSpan group. Counting against `displayRows` (rather than the full
  // `rows`) makes a multi-tenant unit whose group crosses a page boundary still
  // render its Unit Name / Water Usage cells on the page where the visible portion
  // starts, with rowSpan equal to just the visible portion.
  const visibleSpanCounts = displayRows.reduce<Record<string, number>>((acc, r) => {
    acc[r.unitId] = (acc[r.unitId] || 0) + 1;
    return acc;
  }, {});

  const rowsPerPageOptions = [5, 10, 20, { label: 'All', value: rows.length }];

  type RowType = (typeof rows)[number];

  // Compute selectable rows (only rows with invoiceId can be selected for PDF download)
  const selectableRows = rows.filter((r) => r.invoiceId !== null);
  const selectableRowIds = selectableRows.map((r) => r.invoiceId as string);
  const allSelected =
    selectableRowIds.length > 0 && selectedInvoiceIds
      ? selectableRowIds.every((id) => selectedInvoiceIds.has(id))
      : false;
  const someSelected = selectedInvoiceIds
    ? selectableRowIds.some((id) => selectedInvoiceIds.has(id)) && !allSelected
    : false;

  // Handler for select all checkbox (now we have access to rows)
  const handleSelectAllRows = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSelectionChange) return;
    if (event.target.checked) {
      onSelectionChange(new Set(selectableRowIds));
    } else {
      onSelectionChange(new Set());
    }
  };

  return (
    <div ref={targetRef}>
      {/* Selection toolbar — shown when checkboxes are visible */}
      {showSelectionCheckboxes && (onDownloadSelected || onSendEmail) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            mt: 2,
            backgroundColor:
              selectedInvoiceIds && selectedInvoiceIds.size > 0 ? 'action.selected' : 'grey.50',
            borderRadius: '8px 8px 0 0',
            border: 1,
            borderBottom: 0,
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {selectedInvoiceIds && selectedInvoiceIds.size > 0
              ? `${selectedInvoiceIds.size} of ${selectableRowIds.length} selected`
              : `${selectableRowIds.length} invoices`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onDownloadSelected && (
              <SWLoadingButtonOutlined
                size="small"
                onClick={onDownloadSelected}
                disabled={!selectedInvoiceIds || selectedInvoiceIds.size === 0}
                startIcon={<SimCardDownloadOutlinedIcon />}
              >
                Download ({selectedInvoiceIds?.size ?? 0})
              </SWLoadingButtonOutlined>
            )}
            {onSendEmail && (
              <SWLoadingButtonOutlined
                size="small"
                onClick={onSendEmail}
                loading={isSendingEmails}
                disabled={!selectedInvoiceIds || selectedInvoiceIds.size === 0}
                startIcon={<EmailOutlinedIcon />}
              >
                Email ({selectedInvoiceIds?.size ?? 0})
              </SWLoadingButtonOutlined>
            )}
          </Box>
        </Box>
      )}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 'none',
          borderTop: showSelectionCheckboxes && (onDownloadSelected || onSendEmail) ? 0 : 1,
          borderBottom: 1,
          borderColor: 'divider',
          mt: showSelectionCheckboxes && (onDownloadSelected || onSendEmail) ? 0 : 2,
        }}
      >
        <Table size="medium" aria-label="simple table">
          <TableHead>
            <TableRow>
              {/* Checkbox column for batch selection */}
              {showSelectionCheckboxes && (
                <TableCell sx={{ ...cellSX, width: 50 }} align="center" padding="checkbox">
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={handleSelectAllRows}
                    inputProps={{ 'aria-label': 'select all invoices' }}
                  />
                </TableCell>
              )}
              <TableCell sx={cellSX} align="center">
                Unit Name
              </TableCell>
              <TableCell sx={cellSX} align="center">
                Water Usage
              </TableCell>
              {showTenantColumn && (
                <TableCell sx={cellSX} align="center">
                  Tenant
                </TableCell>
              )}
              <TableCell sx={cellSX} align="center">
                Volume Unit
              </TableCell>
              <TableCell sx={cellSX} align="center">
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
                >
                  Unit Cost
                  <Tooltip
                    title="Water usage cost plus any fees for this unit (excludes common area charges)."
                    arrow
                    placement="top"
                  >
                    <IconButton size="small" sx={{ p: 0, ml: 0.5 }}>
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: '17px',
                          color: '#555',
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell sx={cellSX} align="center">
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
                >
                  Invoice Amount
                  <Tooltip
                    title="Total invoice amount including unit cost plus split common area charges. This is what appears on the tenant's invoice."
                    arrow
                    placement="top"
                  >
                    <IconButton size="small" sx={{ p: 0, ml: 0.5 }}>
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: '17px',
                          color: '#555',
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              {/* Due Date column shown for Stripe accounts OR non-Stripe with generated invoices */}
              {showDueDateColumn && (
                <TableCell sx={cellSX} align="center">
                  Due Date
                </TableCell>
              )}
              {showEmailSentColumn && (
                <TableCell sx={cellSX} align="center">
                  Invoice Sent
                </TableCell>
              )}
              {/* Status column only shown for Stripe-connected accounts */}
              {showStatusColumn && (
                <TableCell sx={cellSX} align="center">
                  Status
                </TableCell>
              )}
              {showInvoiceColumns && (
                <TableCell sx={cellSX} align="center">
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                    }}
                  >
                    Invoice
                    {sharedPaymentInfo &&
                      (sharedPaymentInfo.payOnlineUrl || sharedPaymentInfo.paymentInstructions) && (
                        <Tooltip
                          title={
                            <Box>
                              {sharedPaymentInfo.payOnlineUrl && (
                                <Box
                                  sx={{
                                    mb: sharedPaymentInfo.paymentInstructions ? 0.5 : 0,
                                  }}
                                >
                                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    Pay Online URL:
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{ wordBreak: 'break-all' }}
                                  >
                                    {sharedPaymentInfo.payOnlineUrl}
                                  </Typography>
                                </Box>
                              )}
                              {sharedPaymentInfo.paymentInstructions && (
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    Comments:
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{ whiteSpace: 'pre-wrap' }}
                                  >
                                    {sharedPaymentInfo.paymentInstructions}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <IconButton
                            size="small"
                            sx={{ p: 0, ml: 0.5 }}
                            aria-label="payment info used on these invoices"
                          >
                            <InfoOutlinedIcon sx={{ fontSize: '17px', color: '#555' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                  </Box>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          {/* TABLE BODY HANDLING PRORATED UNITS W/ MULTIPLE TENANTS */}
          <TableBody>
            {displayRows.map((row: RowType, idx) => {
              // figure out if this is the first time we've seen this unit in the full `rows` array
              // When showing all rows, idx is the actual index
              // Compare against the previous row WITHIN the visible page (not the
              // global rows array). This way a multi-tenant unit whose group is
              // split across pages still renders its rowSpan'd cells at the top of
              // the visible portion on the second page, instead of leaving those
              // cells stranded on the previous page.
              const localPrev = idx > 0 ? displayRows[idx - 1] : null;
              const isFirstOfUnit = !localPrev || row.unitId !== localPrev.unitId;
              const rowSpan = visibleSpanCounts[row.unitId];

              return (
                <TableRow key={row.id}>
                  {/* Checkbox cell for batch selection */}
                  {showSelectionCheckboxes && (
                    <TableCell align="center" padding="checkbox">
                      {row.invoiceId ? (
                        <Checkbox
                          checked={selectedInvoiceIds?.has(row.invoiceId) ?? false}
                          onChange={(e) => handleSelectRow(row.invoiceId!, e.target.checked)}
                          inputProps={{ 'aria-label': `select invoice for ${row.unitName}` }}
                        />
                      ) : (
                        <Checkbox disabled />
                      )}
                    </TableCell>
                  )}
                  {isFirstOfUnit && (
                    <>
                      <TableCell rowSpan={rowSpan} align="center">
                        {row.unitName}
                      </TableCell>
                      <TableCell rowSpan={rowSpan} align="center">
                        {row.estimatedUsage > 0 ? (
                          <Tooltip
                            title={
                              <Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    minWidth: 150,
                                  }}
                                >
                                  <Typography variant="caption" sx={{ mr: 2 }}>
                                    Metered:
                                  </Typography>
                                  <Typography variant="caption">
                                    {formatNumber(row.usage, 3)}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="caption" sx={{ mr: 2 }}>
                                    Estimated:
                                  </Typography>
                                  <Typography variant="caption">
                                    {formatNumber(row.estimatedUsage, 3)}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    mt: 0.5,
                                    pt: 0.5,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    Total:
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    {formatNumber(row.usage + row.estimatedUsage, 3)}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                            arrow
                            placement="left"
                          >
                            <Typography
                              component="span"
                              sx={{
                                cursor: 'help',
                                borderBottom: '1px dotted rgba(0, 0, 0, 0.3)',
                                '&:hover': {
                                  borderBottomColor: 'primary.main',
                                },
                              }}
                            >
                              {formatNumber(row.usage + row.estimatedUsage, 3)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          formatNumber(row.usage, 3)
                        )}
                      </TableCell>
                    </>
                  )}
                  {/* Render TENANTS when invoices exist */}
                  {showTenantColumn && (
                    <TableCell align="center">
                      {(() => {
                        // Placeholder rows (no invoice yet) show who WILL be invoiced,
                        // from the same contact selection the backend applies at
                        // generation time; partial-period tenants get their clipped
                        // occupancy window as a caption.
                        const prospective =
                          row.invoiceId === null
                            ? billingContactWindowsByUnit.get(row.unitId)
                            : undefined;
                        if (!prospective || prospective.length === 0) return row.tenant;
                        return (
                          <Stack spacing={0.5} alignItems="center">
                            {prospective.map((w) => {
                              const sub = subscriberById.get(w.subscriberId);
                              return (
                                <Box key={`${w.subscriberId}-${w.windowStartIso}`}>
                                  <Typography fontSize="14px">
                                    {sub ? `${sub.first_name} ${sub.last_name}` : 'Tenant'}
                                  </Typography>
                                  {w.isPartialPeriod && (
                                    <Typography fontSize="12px" color="text.secondary">
                                      {dayjs(w.windowStartIso).format('MMM D')} -{' '}
                                      {dayjs(w.windowEndIso).format('MMM D')}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                        );
                      })()}
                    </TableCell>
                  )}
                  {/* render every time */}
                  <TableCell align="center">
                    {UNIT_LABELS[row.volumeUnit as keyof typeof UNIT_LABELS]}
                  </TableCell>

                  {/* Unit Cost column - water usage + fees (excludes common area) */}
                  <TableCell align="center">
                    <Tooltip
                      title={
                        <Box>
                          {getBreakdownItems(row, false).map((item, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                minWidth: 150,
                              }}
                            >
                              <Typography variant="caption" sx={{ mr: 2 }}>
                                {item.label}:
                              </Typography>
                              <Typography variant="caption">
                                ${formatNumber(item.amount, 2)}
                              </Typography>
                            </Box>
                          ))}
                          <Box
                            sx={{
                              borderTop: 1,
                              borderColor: 'divider',
                              mt: 0.5,
                              pt: 0.5,
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              Total:
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              ${formatNumber(row.unitCost, 2)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      arrow
                      placement="left"
                    >
                      <Typography
                        component="span"
                        sx={{
                          cursor: 'help',
                          borderBottom: '1px dotted rgba(0, 0, 0, 0.3)',
                          '&:hover': {
                            borderBottomColor: 'primary.main',
                          },
                        }}
                      >
                        ${formatNumber(row.unitCost, 2)}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Invoice Amount column - full amount including common area split */}
                  <TableCell align="center">
                    <Tooltip
                      title={
                        <Box>
                          {getBreakdownItems(row, true).map((item, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                minWidth: 150,
                              }}
                            >
                              <Typography variant="caption" sx={{ mr: 2 }}>
                                {item.label}:
                              </Typography>
                              <Typography variant="caption">
                                ${formatNumber(item.amount, 2)}
                              </Typography>
                            </Box>
                          ))}
                          <Box
                            sx={{
                              borderTop: 1,
                              borderColor: 'divider',
                              mt: 0.5,
                              pt: 0.5,
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              Total:
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              ${formatNumber(row.invoiceAmount, 2)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      arrow
                      placement="left"
                    >
                      <Typography
                        component="span"
                        sx={{
                          cursor: 'help',
                          borderBottom: '1px dotted rgba(0, 0, 0, 0.3)',
                          '&:hover': {
                            borderBottomColor: 'primary.main',
                          },
                        }}
                      >
                        ${formatNumber(row.invoiceAmount, 2)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  {/* Due Date column shown for Stripe accounts OR non-Stripe with generated invoices */}
                  {showDueDateColumn && (
                    <TableCell align="center">
                      {/* dueDate is end-of-day in the property timezone; format it
                          there too, or viewers east of the property see the next day */}
                      {formatDueDate(row.dueDate, timeZone)}
                    </TableCell>
                  )}
                  {showEmailSentColumn && (
                    <TableCell align="center">
                      {row.emailSentAt
                        ? dayjs.unix(row.emailSentAt).tz(tz).format('MM/DD/YYYY h:mm A')
                        : '—'}
                    </TableCell>
                  )}

                  {/* Render STATUS only for Stripe-connected accounts */}
                  {showStatusColumn && (
                    <TableCell align="center">
                      <StatusChip status={row.status} />
                    </TableCell>
                  )}
                  {/* Render INVOICE column when invoices exist (Stripe or custom) */}
                  {showInvoiceColumns && (
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {/* Stripe Invoice PDF */}
                        {row.pdfUrl ? (
                          <Tooltip title="Download Stripe Invoice" arrow>
                            <IconButton
                              onClick={() => row.pdfUrl && (window.location.href = row.pdfUrl)}
                              size="small"
                            >
                              <SimCardDownloadOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {/* Custom Invoice PDF - show download if already generated */}
                        {row.customPdfUrl ? (
                          <Tooltip title="Download SSW Invoice" arrow>
                            <IconButton
                              onClick={() => window.open(row.customPdfUrl!, '_blank')}
                              size="small"
                            >
                              <SimCardDownloadOutlinedIcon color="primary" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {/* Per-row payment-info snapshot, shown only when invoices
                            in this bill diverge from each other (e.g. a single-row
                            regeneration with a different URL). When all invoices
                            share one URL, the column header tooltip covers it. */}
                        {hasMixedPaymentInfo && row.invoiceId && row.paymentInfoSnapshot && (
                          <Tooltip
                            title={
                              <Box>
                                {row.paymentInfoSnapshot.payOnlineUrl && (
                                  <Box
                                    sx={{
                                      mb: row.paymentInfoSnapshot.paymentInstructions ? 0.5 : 0,
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                      Pay Online URL:
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      component="div"
                                      sx={{ wordBreak: 'break-all' }}
                                    >
                                      {row.paymentInfoSnapshot.payOnlineUrl}
                                    </Typography>
                                  </Box>
                                )}
                                {row.paymentInfoSnapshot.paymentInstructions && (
                                  <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                      Comments:
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      component="div"
                                      sx={{ whiteSpace: 'pre-wrap' }}
                                    >
                                      {row.paymentInfoSnapshot.paymentInstructions}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            }
                            arrow
                            placement="left"
                          >
                            <IconButton size="small" aria-label="payment info for this invoice">
                              <InfoOutlinedIcon fontSize="small" sx={{ color: '#555' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!row.customPdfUrl && row.invoiceId ? (
                          <Tooltip
                            title={
                              pendingCustomInvoiceIds.has(row.invoiceId)
                                ? 'SSW Invoice generating...'
                                : 'Generate SSW Invoice'
                            }
                            arrow
                          >
                            <span>
                              <IconButton
                                onClick={() => openGeneratePdfDialog(row.billingId, row.invoiceId!)}
                                size="small"
                                disabled={
                                  generatingPdfFor === row.invoiceId ||
                                  pendingCustomInvoiceIds.has(row.invoiceId)
                                }
                              >
                                {generatingPdfFor === row.invoiceId ||
                                pendingCustomInvoiceIds.has(row.invoiceId) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <DescriptionOutlinedIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                        {/* Show placeholder if no invoice at all */}
                        {!row.pdfUrl && !row.customPdfUrl && !row.invoiceId && (
                          <Typography>-</Typography>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {!shouldShowAll && (
          <TablePagination
            component="div"
            rowsPerPageOptions={rowsPerPageOptions}
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </TableContainer>
      {/* WHEN: View Bill, After Creating a Bill, and "Send Invoices to Tenants" */}
      {/* BOOLEANS: billing is enabled, have the send invoice button, invoices arent sent */}
      {billingEnabled && needSendBillButton && !billsSentTimestamp && (
        <Box
          sx={{
            mt: 2,
            px: 1,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#E6F7FF', // blue background
            color: '#096DD9', // dark-blue text
            borderRadius: 3,
          }}
        >
          <InfoOutlinedIcon fontSize="small" sx={{ mr: 0.7 }} />
          <Typography component="span">
            Make sure your tenant contact information is up to date before sending invoices!
          </Typography>
        </Box>
      )}
      {/* WHEN: View Bill, After Creating a Bill, and "Send Invoices to Tenants" */}
      {/* BOOLEANS: billing is enabled, have the send invoice button, invoices arent sent */}
      {billingEnabled && needSendBillButton && !billsSentTimestamp && (
        <Box
          sx={{
            mt: 2,
            px: 1,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#E6F7FF', // blue background
            color: '#096DD9', // dark-blue text
            borderRadius: 3,
          }}
        >
          <InfoOutlinedIcon fontSize="small" sx={{ mr: 0.7 }} />
          <Typography component="span">
            You've been granted early access to SimpleSUB Billing. Creating invoices, sending
            invoices, and payment collection are currently in beta.
          </Typography>
        </Box>
      )}

      {/* Generate SSW Invoice PDF Dialog */}
      <Dialog
        open={generatePdfDialogOpen}
        onClose={handleDialogCancel}
        aria-labelledby="generate-invoice-pdf-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="generate-invoice-pdf-dialog" sx={{ fontWeight: 700, fontSize: '20px' }}>
          Generate SSW Invoice PDF
          <IconButton
            aria-label="close"
            onClick={handleDialogCancel}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Configure payment settings for this invoice. These will appear on the generated PDF.
          </DialogContentText>

          {/* Pay Online URL */}
          <PayOnlineUrlField
            value={dialogPayOnlineUrl}
            onChange={(next) => {
              setDialogPayOnlineUrl(next);
              setDialogUrlTouched(true);
            }}
            structureId={bill.structure_id}
            sx={{ mb: 3 }}
            disabled={isSavingSettings || generatingPdfFor !== null}
          />

          {/* Comments */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Comments"
            value={dialogPaymentInstructions}
            onChange={(e) => {
              setDialogPaymentInstructions(e.target.value);
              setDialogInstructionsTouched(true);
            }}
            placeholder="Enter comments for tenants..."
            helperText="Comments displayed on the invoice (optional)"
            sx={{ mb: 2 }}
            disabled={isSavingSettings || generatingPdfFor !== null}
          />

          {/* Save as Default Checkbox */}
          {onPaymentSettingsUpdate && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={saveAsDefault}
                  onChange={(e) => setSaveAsDefault(e.target.checked)}
                  disabled={isSavingSettings || generatingPdfFor !== null}
                />
              }
              label="Save as default for this property"
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SWLoadingButtonOutlined
            onClick={handleDialogCancel}
            disabled={isSavingSettings || generatingPdfFor !== null}
          >
            Cancel
          </SWLoadingButtonOutlined>
          <SWLoadingButton
            onClick={handleDialogConfirm}
            loading={isSavingSettings || generatingPdfFor !== null}
          >
            Generate PDF
          </SWLoadingButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};
